import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Homepage from "./components/Homepage";
import Onboarding from "./components/onboarding/Onboarding";
import SimpleCryptoLogo from "./components/SimpleCryptoLogo";
import axios from "axios";

// Import debounce for search optimization with proper cancellation
const debounce = (func, delay) => {
  let timeoutId;
  const debouncedFn = (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
  
  // Add cancel method to clear pending timeouts
  debouncedFn.cancel = () => {
    clearTimeout(timeoutId);
  };
  
  return debouncedFn;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

// Helper function to format any number with commas for 6+ digits
const formatWithCommas = (num, decimals = 2) => {
  if (num === null || num === undefined || isNaN(num)) {
    return 'N/A';
  }
  const formatted = num.toFixed(decimals);
  const parts = formatted.split('.');
  // Add commas for numbers with 6 or more digits before decimal
  if (parts[0].length >= 6) {
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  return parts.join('.');
};

// Utility function for CoinMarketCap-style price formatting with grayed-out leading zeros and thousand separators
const formatCryptoPrice = (price) => {
  if (price === null || price === undefined || price <= 0) {
    return '—';
  }
  
  const numPrice = typeof price === 'string' ? parseFloat(price) : price;
  
  if (isNaN(numPrice)) {
    return '—';
  }
  
  // Helper function to add thousand separators (commas)
  const addThousandSeparators = (numStr) => {
    const parts = numStr.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  };
  
  // Apply CoinMarketCap-style precision rules
  if (numPrice >= 1) {
    // ≥ $1.00: 2 decimal places with thousand separators
    const formatted = numPrice.toFixed(2);
    return addThousandSeparators(formatted);
  } else if (numPrice >= 0.01) {
    // $1.00 > price ≥ $0.01: 4 decimal places (no commas needed for small numbers)
    return numPrice.toFixed(4);
  } else if (numPrice >= 0.0001) {
    // $0.01 > price ≥ $0.0001: 6 decimal places (no commas needed for small numbers)
    return numPrice.toFixed(6);
  } else if (numPrice >= 0.00001) {
    // $0.0001 > price ≥ $0.00001: 8 decimal places
    return numPrice.toFixed(8);
  } else {
    // price < $0.00001: handle ultra-small prices with first 4 significant digits after leading zeros
    
    // Convert to string to handle scientific notation properly
    let priceStr = numPrice.toString();
    
    // Handle scientific notation (e.g., 1.2429413e-12)
    if (priceStr.includes('e-')) {
      const [mantissa, exponent] = priceStr.split('e-');
      const exp = parseInt(exponent);
      // Use enough decimal places to capture the value accurately
      const decimals = Math.max(20, exp + 10);
      priceStr = numPrice.toFixed(Math.min(decimals, 50));
    } else {
      // For regular small numbers, use high precision
      priceStr = numPrice.toFixed(50);
    }
    
    // Remove trailing zeros
    priceStr = priceStr.replace(/\.?0+$/, '');
    
    // Extract decimal part
    const decimalPart = priceStr.split('.')[1] || '';
    if (decimalPart.length === 0) {
      return '0.00';
    }
    
    // Find leading zeros and first 4 significant digits
    let leadingZeroCount = 0;
    let significantDigits = '';
    let significantCount = 0;
    
    for (let i = 0; i < decimalPart.length; i++) {
      if (decimalPart[i] === '0' && significantCount === 0) {
        leadingZeroCount++;
      } else {
        significantDigits += decimalPart[i];
        significantCount++;
        if (significantCount === 4) break; // Only take first 4 significant digits
      }
    }
    
    // Build the formatted result
    const leadingZeros = '0.' + '0'.repeat(leadingZeroCount);
    const formattedPrice = leadingZeros + significantDigits;
    
    // Return JSX element with grayed out leading zeros for display
    return {
      type: 'formatted',
      leadingZeros: leadingZeros,
      significantDigits: significantDigits,
      fullValue: formattedPrice
    };
  }
};

// Component for rendering formatted price with grayed-out leading zeros
const FormattedPrice = ({ priceData, className = "" }) => {
  if (typeof priceData === 'string') {
    return <span className={className}>{priceData}</span>;
  }
  
  if (priceData && priceData.type === 'formatted') {
    return (
      <span className={className}>
        <span className="price-leading-zeros">{priceData.leadingZeros}</span>
        <span className="price-significant">{priceData.significantDigits}</span>
      </span>
    );
  }
  
  return <span className={className}>{priceData}</span>;
};

// Utility function for formatting percentage changes
const formatPercentageChange = (change) => {
  if (change === null || change === undefined) {
    return '0.00%';
  }
  return `${change >= 0 ? '+' : ''}${change.toFixed(2)}%`;
};

// Utility function for formatting technical indicator values
const formatIndicatorValue = (value, precision = 2) => {
  if (value === null || value === undefined || isNaN(value)) {
    return 'N/A';
  }
  const num = parseFloat(value);
  // Use formatWithCommas for better readability of large numbers
  return formatWithCommas(num, precision);
};

const IndicatorModal = ({ isOpen, onClose, symbol, cryptoName }) => {
  const [loading, setLoading] = useState(false);
  const [indicators, setIndicators] = useState({});
  const [aiSummary, setAiSummary] = useState(null);
  const [cryptoData, setCryptoData] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [currentLoadingPhrase, setCurrentLoadingPhrase] = useState("");
  const [userProfile, setUserProfile] = useState(null);
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [newsData, setNewsData] = useState(null);
  const [articlesToShow, setArticlesToShow] = useState(3);
  const [sentimentData, setSentimentData] = useState(null);
  const [isNewsLoading, setIsNewsLoading] = useState(false);

  if (!isOpen) return null;

  const loadingPhrases = [
    "Gathering Real Time Crypto Data…",
    "Analyzing Indicators…",
    "Analyzing MACD…",
    "Analyzing Bollinger Bands…",
    "Analyzing RSI…",
    "Analyzing Price Action…",
    "Analyzing Volume Trends…",
    "Calculating Moving Averages…",
    "Processing Technical Signals…",
    "Evaluating Market Momentum…",
    "Analyzing ADX Trends…",
    "Computing Williams %R…",
    "Personalizing Analysis for Your Profile…",
    "Finalizing Crypto Analysis…"
  ];

  const getRandomLoadingPhrase = () => {
    return loadingPhrases[Math.floor(Math.random() * loadingPhrases.length)];
  };

  // Fetch user profile data for personalized analysis
  const fetchUserProfile = async () => {
    try {
      // First try to get from localStorage (most reliable for this use case)
      const storedProfile = localStorage.getItem('userProfile');
      if (storedProfile) {
        const profile = JSON.parse(storedProfile);
        setUserProfile(profile);
        console.log('User profile loaded from localStorage for personalized analysis:', profile);
        return profile;
      }
      
      // Fallback: try to get from AuthContext if available
      // Note: This would require importing useAuth, but for now we'll use localStorage
      console.log('No user profile available in localStorage for personalized analysis');
      return null;
    } catch (error) {
      console.log('No user profile available for personalized analysis');
      return null;
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setCurrentLoadingPhrase(getRandomLoadingPhrase());
      
      const phraseInterval = setInterval(() => {
        setCurrentLoadingPhrase(getRandomLoadingPhrase());
      }, 800);

      // Fetch user profile for personalized analysis
      const userProfileData = await fetchUserProfile();

      // Fetch indicators and crypto data
      const [indicatorsResponse, cryptoResponse] = await Promise.all([
        axios.get(`${API}/cryptos/${symbol}/indicators`),
        axios.get(`${API}/cryptos/${symbol}`)
      ]);
      
      setIndicators(indicatorsResponse.data.indicators);
      setCryptoData(cryptoResponse.data.crypto);
      setProfileData(cryptoResponse.data.profile);
      
      // AI summary will be loaded separately after main data is displayed
      
      clearInterval(phraseInterval);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch news and sentiment data for the selected cryptocurrency
  const fetchNewsAndSentiment = async () => {
    console.log('🚀 fetchNewsAndSentiment called for symbol:', symbol);
    try {
      setIsNewsLoading(true);
      
      // Clear previous data immediately to prevent showing stale data
      console.log('🧹 Clearing previous news and sentiment data...');
      setNewsData([]);
      setSentimentData(null);
      setArticlesToShow(3); // Reset to show 3 articles by default
      
      // Get the base symbol (remove USD suffix)
      const baseSymbol = symbol.replace(/USD$/, '');
      
      console.log(`📰 Fetching news and sentiment for ${symbol}...`);
      
      // Fetch news and sentiment data from FMP API (with cache busting)
      const cacheBuster = Date.now();
      const newsUrl = `${API}/cryptos/${symbol}/news?v=${cacheBuster}`;
      const sentimentUrl = `${API}/cryptos/${symbol}/sentiment?v=${cacheBuster}`;
      console.log(`🔗 News API URL: ${newsUrl}`);
      console.log(`🔗 Sentiment API URL: ${sentimentUrl}`);
      console.log(`🌐 Full API base: ${API}`);
      console.log(`🌐 Backend URL: ${BACKEND_URL}`);
      
      // Test if we can reach the backend at all
      try {
        console.log('🔍 Testing backend connectivity...');
        const testResponse = await axios.get(`${BACKEND_URL}/api/`, { timeout: 5000 });
        console.log('✅ Backend connection test successful:', testResponse.status);
      } catch (testError) {
        console.error('❌ Backend connectivity test failed:', testError.message);
        console.error('❌ Test error details:', testError);
      }
      
      console.log('📡 Making API calls...');
      // Test individual requests to debug which one is failing
      console.log('🔍 Testing news request first...');
      const newsResponse = await axios.get(newsUrl, { 
        timeout: 30000,  // Increased to 30 seconds
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      console.log('✅ News request successful:', newsResponse.data);
      
      console.log('🔍 Testing sentiment request...');
      const sentimentResponse = await axios.get(sentimentUrl, { 
        timeout: 30000,  // Increased to 30 seconds
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      console.log('✅ Sentiment request successful:', sentimentResponse.data);
      
      console.log('✅ API calls completed successfully');
      console.log(`📰 News response status:`, newsResponse.status);
      console.log(`📊 Sentiment response status:`, sentimentResponse.status);
      console.log(`📰 News response:`, newsResponse.data);
      console.log(`📊 Sentiment response:`, sentimentResponse.data);
      
      console.log('News data type:', typeof newsResponse.data);
      console.log('News data is array:', Array.isArray(newsResponse.data));
      console.log('News data length:', newsResponse.data?.length);
      console.log('First news item:', newsResponse.data?.[0]);
      
      console.log('💾 Setting news data:', newsResponse.data);
      console.log('💾 Setting sentiment data:', sentimentResponse.data);
      console.log('✅ API call successful - using real data');
      
      // Verify news articles are for the correct symbol
      if (newsResponse.data && Array.isArray(newsResponse.data)) {
        console.log(`🔍 Verifying news articles are for ${symbol}:`);
        newsResponse.data.slice(0, 3).forEach((article, index) => {
          console.log(`  Article ${index + 1}: "${article.title}" (symbol: ${article.symbol})`);
        });
      }
      
      setNewsData(newsResponse.data);
      setSentimentData(sentimentResponse.data);
      console.log('✅ State updated - newsData and sentimentData set');
      
    } catch (error) {
      console.error('❌ Error fetching news and sentiment:', error);
      console.error('❌ Error details:', error.response?.data || error.message);
      console.error('❌ Error status:', error.response?.status);
      console.error('❌ Error URL:', error.config?.url);
      console.error('❌ Full error:', error);
      console.error('❌ Request config:', error.config);
      
      // TEMPORARILY DISABLED FALLBACK TO DEBUG THE REAL ISSUE
      // Instead, set empty arrays so we can see what's actually failing
      setNewsData([]);
      setSentimentData(null);
      
      // Alert the user about the real error for debugging
      console.error('🚨 REAL ERROR - NO FALLBACK:', error.message);
      alert(`NEWS ERROR: ${error.message} - Check console for details`);
      console.log('✅ Fallback news data set due to API timeout');
    } finally {
      setIsNewsLoading(false);
    }
  };

  useEffect(() => {
    console.log('🔄 useEffect triggered - isOpen:', isOpen, 'symbol:', symbol);
    if (isOpen && symbol) {
      console.log('✅ Modal is open and symbol exists, fetching data...');
      // Reset data when symbol changes to show loading spinner
      setCryptoData(null);
      setProfileData(null);
      setIndicators({});
      setAiSummary(null);
      setNewsData(null);
      setSentimentData(null);
      
      // Fetch new data
      console.log('📡 Calling fetchData()...');
      fetchData();
      console.log('📡 Calling fetchNewsAndSentiment()...');
      fetchNewsAndSentiment();
      console.log('✅ Both fetchData and fetchNewsAndSentiment called');
    } else {
      console.log('❌ Modal not open or symbol missing - isOpen:', isOpen, 'symbol:', symbol);
    }
  }, [isOpen, symbol]);

  // Load AI summary only after main data has loaded and insight panel is displayed
  useEffect(() => {
    if (indicators && Object.keys(indicators).length > 0 && cryptoData && !aiSummary && !isAiLoading) {
      const loadAISummary = async () => {
        try {
          setIsAiLoading(true);
          const summary = await getAISummary();
          setAiSummary(summary);
        } catch (aiError) {
          console.log('AI summary not available:', aiError);
          setAiSummary("AI analysis temporarily unavailable. Please try again later.");
        } finally {
          setIsAiLoading(false);
        }
      };
      
      // Add a small delay to ensure the main panel is fully rendered first
      setTimeout(loadAISummary, 500);
    }
  }, [indicators, cryptoData, aiSummary, isAiLoading]);

  const getBias = () => {
    if (!indicators.rsi || !indicators.macd) return 'neutral';
    
    let bullishCount = 0;
    let bearishCount = 0;
    
    // RSI analysis
    if (indicators.rsi > 50) bullishCount++;
    else bearishCount++;
    
    // MACD analysis
    if (indicators.macd && indicators.macd.macd > indicators.macd.signal) bullishCount++;
    else bearishCount++;
    
    if (bullishCount > bearishCount) return 'bullish';
    if (bearishCount > bullishCount) return 'bearish';
    return 'neutral';
  };

  const getIndicatorInterpretation = (indicator, value) => {
    if (!value || typeof value !== 'number') return 'No data available';
    
    switch (indicator) {
      case 'rsi':
        if (value > 70) return 'Overbought';
        if (value < 30) return 'Oversold';
        return 'Neutral';
      case 'macd':
        return value > 0 ? 'Bullish' : 'Bearish';
      default:
        return 'Neutral';
    }
  };

  const getRSIAnalysis = (period = 14) => {
    const rsiKey = period === 7 ? 'rsi_7' : period === 21 ? 'rsi_21' : 'rsi_14';
    const rsi = indicators[rsiKey];
    
    if (!rsi || typeof rsi !== 'number') {
      return "RSI data unavailable — unable to determine momentum strength.";
    }
    
    if (rsi >= 70) {
      return `RSI at ${rsi.toFixed(1)} shows overbought conditions — exercise caution as reversal risk increases.`;
    } else if (rsi >= 60) {
      return `RSI at ${rsi.toFixed(1)} indicates strong bullish bias — confident momentum with room for continued strength.`;
    } else if (rsi >= 53) {
      return `RSI at ${rsi.toFixed(1)} shows slight bullish bias — cautious optimism with moderate upward pressure.`;
    } else if (rsi >= 48) {
      return `RSI at ${rsi.toFixed(1)} indicates neutral conditions — no clear directional signal at this time.`;
    } else if (rsi >= 41) {
      return `RSI at ${rsi.toFixed(1)} shows slight bearish bias — cautious warning with moderate downward pressure.`;
    } else if (rsi >= 30) {
      return `RSI at ${rsi.toFixed(1)} indicates strong bearish bias — confident downward momentum with limited upside.`;
    } else {
      return `RSI at ${rsi.toFixed(1)} shows oversold conditions — exercise caution as reversal potential increases.`;
    }
  };

  const getRSISummary = (period) => {
    const rsiKey = period === 7 ? 'rsi_7' : period === 21 ? 'rsi_21' : 'rsi_14';
    const rsi = indicators[rsiKey];
    
    if (!rsi || typeof rsi !== 'number') {
      return "Data unavailable";
    }
    
    if (period === 7) {
      if (rsi > 70) return "Short-term momentum is increasing.";
      if (rsi > 50) return "Short-term momentum is strong.";
      return "Short-term momentum is weak.";
    } else if (period === 14) {
      if (rsi > 70) return "Swing bias shows strength.";
      if (rsi > 50) return "Swing bias is bullish.";
      return "Swing bias is bearish.";
    } else if (period === 21) {
      if (rsi > 70) return "Smoothed confirmation of uptrend.";
      if (rsi > 50) return "Smoothed trend is positive.";
      return "Smoothed trend is negative.";
    }
    
    return "Momentum analysis unavailable.";
  };

  const getRSIColor = (value) => {
    if (value >= 70) return '#ef4444'; // Red for overbought (70-100)
    if (value >= 60) return '#22c55e'; // Green for strong bullish bias (60-69)
    if (value >= 53) return '#22c55e'; // Green for slight bullish bias (53-59)
    if (value >= 48) return '#3b82f6'; // Blue for neutral (48-52)
    if (value >= 41) return '#f97316'; // Orange for slight bearish bias (41-47)
    if (value >= 30) return '#f97316'; // Orange for strong bearish bias (30-40)
    return '#ef4444'; // Red for oversold (< 30)
  };

  const getRSILabel = (value) => {
    if (value >= 70) return 'Overbought';
    if (value >= 60) return 'Strong Bullish Bias';
    if (value >= 53) return 'Slight Bullish Bias';
    if (value >= 48) return 'Neutral';
    if (value >= 41) return 'Slight Bearish Bias';
    if (value >= 30) return 'Strong Bearish Bias';
    return 'Oversold';
  };

  const getRSIInterpretation = (period, value) => {
    if (!value || typeof value !== 'number') return 'No data';
    
    if (value > 70) return `${period} RSI overbought`;
    if (value < 30) return `${period} RSI oversold`;
    return `${period} RSI neutral`;
  };

  const getRSISummaryAnalysis = () => {
    // Check if we have any RSI indicators available
    const rsi7 = indicators.rsi_7;
    const rsi14 = indicators.rsi_14;
    const rsi21 = indicators.rsi_21;
    
    if (!rsi7 && !rsi14 && !rsi21) {
      return "RSI indicators unavailable — unable to assess momentum strength.";
    }
    
    let bullishCount = 0;
    let bearishCount = 0;
    let neutralCount = 0;
    let overboughtCount = 0;
    let oversoldCount = 0;
    
    // Analyze each RSI period
    [rsi7, rsi14, rsi21].forEach(rsi => {
      if (rsi && typeof rsi === 'number') {
        if (rsi >= 70) {
          overboughtCount++;
        } else if (rsi >= 60) {
          bullishCount++;
        } else if (rsi >= 53) {
          bullishCount++;
        } else if (rsi >= 48) {
          neutralCount++;
        } else if (rsi >= 41) {
          bearishCount++;
        } else if (rsi >= 30) {
          bearishCount++;
        } else {
          oversoldCount++;
        }
      }
    });
    
    // Generate summary based on dominant bias
    if (overboughtCount > 0 && overboughtCount >= bullishCount && overboughtCount >= bearishCount) {
      return "RSI indicators show overbought conditions across multiple timeframes — exercise caution as reversal risk increases.";
    } else if (oversoldCount > 0 && oversoldCount >= bullishCount && oversoldCount >= bearishCount) {
      return "RSI indicators show oversold conditions across multiple timeframes — exercise caution as reversal potential increases.";
    } else if (bullishCount > bearishCount && bullishCount > neutralCount) {
      return "RSI indicators show bullish bias across multiple timeframes — confident momentum with room for continued strength.";
    } else if (bearishCount > bullishCount && bearishCount > neutralCount) {
      return "RSI indicators show bearish bias across multiple timeframes — confident downward momentum with limited upside.";
    } else if (neutralCount > bullishCount && neutralCount > bearishCount) {
      return "RSI indicators are largely neutral across timeframes — no clear directional signal at this time.";
    } else if (bullishCount === bearishCount && bullishCount > 0) {
      return "RSI indicators show conflicting signals with equal bullish and bearish bias — market indecision prevails.";
    } else {
      return "RSI indicators show mixed signals with no clear directional bias — monitor for developing trends or breakout signals.";
    }
  };

  const getRSISummaryBorderColor = () => {
    // Check if we have any RSI indicators available
    const rsi7 = indicators.rsi_7;
    const rsi14 = indicators.rsi_14;
    const rsi21 = indicators.rsi_21;
    
    if (!rsi7 && !rsi14 && !rsi21) {
      return '#6b7280'; // Default gray
    }
    
    let bullishCount = 0;
    let bearishCount = 0;
    let neutralCount = 0;
    let overboughtCount = 0;
    let oversoldCount = 0;
    
    // Analyze each RSI period
    [rsi7, rsi14, rsi21].forEach(rsi => {
      if (rsi && typeof rsi === 'number') {
        if (rsi >= 70) {
          overboughtCount++;
        } else if (rsi >= 60) {
          bullishCount++;
        } else if (rsi >= 53) {
          bullishCount++;
        } else if (rsi >= 48) {
          neutralCount++;
        } else if (rsi >= 41) {
          bearishCount++;
        } else if (rsi >= 30) {
          bearishCount++;
        } else {
          oversoldCount++;
        }
      }
    });
    
    // Return color based on dominant bias
    if (overboughtCount > 0 && overboughtCount >= bullishCount && overboughtCount >= bearishCount) {
      return '#ef4444'; // Red for overbought
    } else if (oversoldCount > 0 && oversoldCount >= bullishCount && oversoldCount >= bearishCount) {
      return '#ef4444'; // Red for oversold
    } else if (bullishCount > bearishCount && bullishCount > neutralCount) {
      return '#22c55e'; // Green for bullish
    } else if (bearishCount > bullishCount && bearishCount > neutralCount) {
      return '#f97316'; // Orange for bearish
    } else if (neutralCount > bullishCount && neutralCount > bearishCount) {
      return '#3b82f6'; // Blue for neutral
    } else {
      return '#6b7280'; // Gray for mixed/conflicting
    }
  };

  // Add a function to map Stochastic %K & Stochastic %D values to color labels and color codes

  /**
   * Returns the label and color for a given Stochastic %K value.
   * 
   * Value ranges:
   * - 0–20: Oversold (Red)
   * - 20–40: Bearish Bias (Orange)
   * - 40–60: Neutral (Blue)
   * - 60–80: Bullish Bias (Green)
   * - 80–100: Overbought (Red)
   */
  const getStochasticKColor = (value) => {
    if (value >= 80) return '#ef4444'; // Red for overbought (80-100)
    if (value >= 60) return '#22c55e'; // Green for bullish bias (60-80)
    if (value >= 40) return '#3b82f6'; // Blue for neutral (40-60)
    if (value >= 20) return '#f97316'; // Orange for bearish bias (20-40)
    return '#ef4444'; // Red for oversold (0-20)
  };

  const getStochasticKLabel = (value) => {
    if (value >= 80) return 'Overbought';
    if (value >= 60) return 'Bullish Bias';
    if (value >= 40) return 'Neutral';
    if (value >= 20) return 'Bearish Bias';
    return 'Oversold';
  };

  /**
   * Returns the label and color for a given Stochastic %D value.
   * 
   * Value ranges:
   * - 0–20: Oversold (Red)
   * - 20–40: Bearish Bias (Orange)
   * - 40–60: Neutral (Blue)
   * - 60–80: Bullish Bias (Green)
   * - 80–100: Overbought (Red)
   */
  const getStochasticDColor = (value) => {
    if (value >= 80) return '#ef4444'; // Red for overbought (80-100)
    if (value >= 60) return '#22c55e'; // Green for bullish bias (60-80)
    if (value >= 40) return '#3b82f6'; // Blue for neutral (40-60)
    if (value >= 20) return '#f97316'; // Orange for bearish bias (20-40)
    return '#ef4444'; // Red for oversold (0-20)
  };

  const getStochasticDLabel = (value) => {
    if (value >= 80) return 'Overbought';
    if (value >= 60) return 'Bullish Bias';
    if (value >= 40) return 'Neutral';
    if (value >= 20) return 'Bearish Bias';
    return 'Oversold';
  };

  const getMomentumAnalysis = () => {
    const stoch_k = indicators.stochastic_k;
    const stoch_d = indicators.stochastic_d;
    const williams = indicators.williams_r;
    const cci = indicators.cci;
    
    // Check if we have any momentum indicators available
    const hasStochasticK = stoch_k !== undefined && stoch_k !== null;
    const hasStochasticD = stoch_d !== undefined && stoch_d !== null;
    const hasWilliams = williams !== undefined && williams !== null;
    const hasCCI = cci !== undefined && cci !== null;
    
    if (!hasStochasticK && !hasStochasticD && !hasWilliams && !hasCCI) {
      return "Momentum indicators unavailable — unable to assess short-term price momentum.";
    }
    
    let bearishCount = 0;
    let bullishCount = 0;
    let neutralCount = 0;
    let totalIndicators = 0;
    
    // Analyze Stochastic %K
    if (hasStochasticK) {
      totalIndicators++;
      if (stoch_k < 20) {
        bearishCount++; // Oversold = bearish
      } else if (stoch_k > 80) {
        bullishCount++; // Overbought = bullish
      } else if (stoch_k >= 60) {
        bullishCount++; // Bullish range
      } else if (stoch_k <= 40) {
        bearishCount++; // Bearish range
      } else {
        neutralCount++; // Neutral range (40-60)
      }
    }
    
    // Analyze Stochastic %D
    if (hasStochasticD) {
      totalIndicators++;
      if (stoch_d < 20) {
        bearishCount++; // Oversold = bearish
      } else if (stoch_d > 80) {
        bullishCount++; // Overbought = bullish
      } else if (stoch_d >= 60) {
        bullishCount++; // Bullish range
      } else if (stoch_d <= 40) {
        bearishCount++; // Bearish range
      } else {
        neutralCount++; // Neutral range (40-60)
      }
    }
    
    // Analyze Williams %R
    if (hasWilliams) {
      totalIndicators++;
      if (williams < -80) {
        bearishCount++; // Oversold = bearish
      } else if (williams > -20) {
        bullishCount++; // Overbought = bullish
      } else if (williams >= -40) {
        bullishCount++; // Bullish range
      } else if (williams <= -60) {
        bearishCount++; // Bearish range
      } else {
        neutralCount++; // Neutral range (-60 to -40)
      }
    }
    
    // Analyze CCI
    if (hasCCI) {
      totalIndicators++;
      if (cci < -100) {
        bearishCount++; // Oversold = bearish
      } else if (cci > 100) {
        bullishCount++; // Overbought = bullish
      } else if (cci >= 50) {
        bullishCount++; // Bullish range
      } else if (cci <= -50) {
        bearishCount++; // Bearish range
      } else {
        neutralCount++; // Neutral range (-50 to 50)
      }
    }
    
    // Determine summary based on data-driven logic
    if (bearishCount >= 3) {
      // Check if values are extremely low (deep oversold)
      const isDeepOversold = (hasStochasticK && stoch_k < 10) || 
                            (hasStochasticD && stoch_d < 10) || 
                            (hasWilliams && williams < -90) || 
                            (hasCCI && cci < -150);
      
      if (isDeepOversold) {
        return "Momentum indicators show overall bearish bias, suggesting potential downward movement with strong downside pressure.";
      } else {
        return "Momentum indicators show overall bearish bias, suggesting potential downward movement with limited strength.";
      }
    } else if (bullishCount >= 3) {
      // Check if values are extremely high (deep overbought)
      const isDeepOverbought = (hasStochasticK && stoch_k > 90) || 
                              (hasStochasticD && stoch_d > 90) || 
                              (hasWilliams && williams > -10) || 
                              (hasCCI && cci > 150);
      
      if (isDeepOverbought) {
        return "Momentum indicators show bullish bias, suggesting upward price pressure with strong momentum signals.";
      } else {
        return "Momentum indicators show bullish bias, suggesting upward price pressure but without strong momentum signals.";
      }
    } else if (bearishCount === 2 && (bullishCount === 2 || (bullishCount === 1 && neutralCount === 1))) {
      return "Momentum signals are mixed, showing limited directional bias and potential consolidation.";
    } else if (neutralCount >= 3 || (neutralCount >= 2 && (bullishCount + bearishCount) <= 1)) {
      return "Momentum indicators show neutral signals — limited direction or trend strength.";
    } else {
      return "Momentum signals are mixed, showing limited directional bias and potential consolidation.";
    }
  };

  const getMomentumSummaryBorderColor = () => {
    const stoch_k = indicators.stochastic_k;
    const stoch_d = indicators.stochastic_d;
    const williams = indicators.williams_r;
    const cci = indicators.cci;
    
    // Check if we have any momentum indicators available
    const hasStochasticK = stoch_k !== undefined && stoch_k !== null;
    const hasStochasticD = stoch_d !== undefined && stoch_d !== null;
    const hasWilliams = williams !== undefined && williams !== null;
    const hasCCI = cci !== undefined && cci !== null;
    
    if (!hasStochasticK && !hasStochasticD && !hasWilliams && !hasCCI) {
      return '#6b7280'; // Default gray
    }
    
    let strongBullishCount = 0;
    let slightBullishCount = 0;
    let neutralCount = 0;
    let slightBearishCount = 0;
    let strongBearishCount = 0;
    let totalIndicators = 0;
    
    // Analyze Stochastic %K
    if (hasStochasticK) {
      totalIndicators++;
      if (stoch_k > 80) {
        strongBullishCount++; // Overbought = strong bullish
      } else if (stoch_k >= 60 && stoch_k <= 80) {
        slightBullishCount++; // Bullish range
      } else if (stoch_k >= 40 && stoch_k < 60) {
        neutralCount++; // Neutral range
      } else if (stoch_k >= 20 && stoch_k < 40) {
        slightBearishCount++; // Bearish range
      } else {
        strongBearishCount++; // Oversold = strong bearish
      }
    }
    
    // Analyze Stochastic %D
    if (hasStochasticD) {
      totalIndicators++;
      if (stoch_d > 80) {
        strongBullishCount++; // Overbought = strong bullish
      } else if (stoch_d >= 60 && stoch_d <= 80) {
        slightBullishCount++; // Bullish range
      } else if (stoch_d >= 40 && stoch_d < 60) {
        neutralCount++; // Neutral range
      } else if (stoch_d >= 20 && stoch_d < 40) {
        slightBearishCount++; // Bearish range
      } else {
        strongBearishCount++; // Oversold = strong bearish
      }
    }
    
    // Analyze Williams %R
    if (hasWilliams) {
      totalIndicators++;
      if (williams > -20) {
        strongBullishCount++; // Overbought = strong bullish
      } else if (williams >= -40 && williams <= -20) {
        slightBullishCount++; // Bullish range
      } else if (williams >= -60 && williams < -40) {
        neutralCount++; // Neutral range
      } else if (williams >= -80 && williams < -60) {
        slightBearishCount++; // Bearish range
      } else {
        strongBearishCount++; // Oversold = strong bearish
      }
    }
    
    // Analyze CCI
    if (hasCCI) {
      totalIndicators++;
      if (cci > 100) {
        strongBullishCount++; // Overbought = strong bullish
      } else if (cci >= 50 && cci <= 100) {
        slightBullishCount++; // Bullish range
      } else if (cci >= -50 && cci < 50) {
        neutralCount++; // Neutral range
      } else if (cci >= -100 && cci < -50) {
        slightBearishCount++; // Bearish range
      } else {
        strongBearishCount++; // Oversold = strong bearish
      }
    }
    
    // Determine majority sentiment and return appropriate color
    const majorityThreshold = Math.ceil(totalIndicators / 2);
    
    // Strong bearish (red)
    if (strongBearishCount >= majorityThreshold) {
      return '#ef4444'; // Red
    }
    
    // Strong bullish (green)
    if (strongBullishCount >= majorityThreshold) {
      return '#22c55e'; // Green
    }
    
    // Slightly bearish (orange)
    if (slightBearishCount >= majorityThreshold) {
      return '#f97316'; // Orange
    }
    
    // Slightly bullish (green - same as strong bullish for consistency)
    if (slightBullishCount >= majorityThreshold) {
      return '#22c55e'; // Green
    }
    
    // Neutral or mixed (blue)
    if (neutralCount >= majorityThreshold || (neutralCount >= 2 && (strongBullishCount + slightBullishCount + strongBearishCount + slightBearishCount) <= 1)) {
      return '#3b82f6'; // Blue
    }
    
    // Mixed signals (blue)
    return '#3b82f6'; // Blue
  };

  // Helper functions for momentum indicators styling
  const getStochasticSummary = (value) => {
    if (value >= 80) return "Overbought momentum zone";
    if (value >= 60) return "Bullish momentum zone";
    if (value >= 40) return "Neutral momentum zone";
    if (value >= 20) return "Bearish momentum zone";
    return "Oversold momentum zone";
  };

  const getStochasticStatus = (value) => {
    return getStochasticKLabel(value);
  };

  const getWilliamsSummary = (value) => {
    if (value >= -20) return "Overbought momentum zone";
    if (value >= -40) return "Bullish momentum zone";
    if (value >= -60) return "Neutral momentum zone";
    if (value >= -80) return "Bearish momentum zone";
    return "Oversold momentum zone";
  };

  const getWilliamsStatus = (value) => {
    if (value >= -20) return "Overbought";
    if (value >= -40) return "Bullish Bias";
    if (value >= -60) return "Neutral";
    if (value >= -80) return "Bearish Bias";
    return "Oversold";
  };

  const getCCISummary = (value) => {
    if (value >= 150) return "Strong upside stretch (reversal possible)";
    if (value >= 100) return "Above average, positive momentum";
    if (value >= -100) return "Normal movement, no strong trend";
    if (value >= -150) return "Below average, downward pressure";
    return "Strong downside stretch (possible bounce)";
  };

  const getCCIStatus = (value) => {
    if (value >= 150) return "Overbought";
    if (value >= 100) return "Bullish Bias";
    if (value >= -100) return "Neutral";
    if (value >= -150) return "Bearish Bias";
    return "Oversold";
  };

  const getMomentumColor = (indicator, value) => {
    switch (indicator) {
      case 'stochastic':
        return getStochasticKColor(value);
      case 'williams':
        if (value >= -20) return '#ef4444'; // Red for overbought (-20 to 0)
        if (value >= -40) return '#22c55e'; // Green for bullish bias (-40 to -20)
        if (value >= -60) return '#3b82f6'; // Blue for neutral (-60 to -40)
        if (value >= -80) return '#f97316'; // Orange for bearish bias (-80 to -60)
        return '#ef4444'; // Red for oversold (-100 to -80)
      case 'cci':
        if (value >= 150) return '#ef4444'; // Red for overbought (≥ +150)
        if (value >= 100) return '#22c55e'; // Green for bullish bias (+100 to +150)
        if (value >= -100) return '#3b82f6'; // Blue for neutral (-100 to +100)
        if (value >= -150) return '#f97316'; // Orange for bearish bias (-150 to -100)
        return '#ef4444'; // Red for oversold (≤ -150)
      default:
        return '#f59e0b';
    }
  };

  // Helper functions for moving averages styling
  const getMASummary = (type, value, currentPrice) => {
    if (!currentPrice) return "Price data unavailable";
    
    const priceDiff = ((currentPrice - value) / value) * 100;
    
    // Get the moving average name for the summary
    const getMAName = (type) => {
      switch (type) {
        case 'ema_12': return '12-period EMA';
        case 'ema_26': return '26-period EMA';
        case 'sma_50': return '50-day SMA';
        case 'sma_200': return '200-day SMA';
        case 'sma_20': return '20-day SMA';
        default: return 'moving average';
      }
    };
    
    const maName = getMAName(type);
    
    // New percentage-based summary mapping for all moving averages
    if (priceDiff > 2) return `Price strongly above ${maName} (>2%)`;
    if (priceDiff > 1) return `Price above ${maName} (1-2%)`;
    if (priceDiff > -1) return `Price near ${maName} (±1%)`;
    if (priceDiff > -2) return `Price below ${maName} (1-2%)`;
    return `Price strongly below ${maName} (>2%)`;
  };

  const getMAStatus = (type, value, currentPrice) => {
    if (!currentPrice) return "Neutral";
    
    const priceDiff = ((currentPrice - value) / value) * 100;
    
    // New percentage-based status mapping for all moving averages
    if (priceDiff > 2) return "Strong Bullish";
    if (priceDiff > 1) return "Light Bullish";
    if (priceDiff > -1) return "Neutral";
    if (priceDiff > -2) return "Light Bearish";
    return "Strong Bearish";
  };

  const getMAColor = (type, value, currentPrice) => {
    if (!currentPrice) return '#3b82f6'; // Blue for neutral
    
    const priceDiff = ((currentPrice - value) / value) * 100;
    
    // New percentage-based color mapping for all moving averages
    if (priceDiff > 2) return '#22c55e'; // Strong Bullish - Green
    if (priceDiff > 1) return '#16a34a'; // Light Bullish - Green
    if (priceDiff > -1) return '#3b82f6'; // Neutral - Blue
    if (priceDiff > -2) return '#f97316'; // Light Bearish - Light Orange
    return '#ef4444'; // Strong Bearish - Red
  };

  // Helper functions for VWAP styling
  const getVWAPSummary = (vwapValue, currentPrice) => {
    if (!currentPrice || !vwapValue) return "VWAP data unavailable";
    
    const priceDiff = ((currentPrice - vwapValue) / vwapValue) * 100;
    
    if (priceDiff > 2) return "Price significantly above VWAP indicating strong institutional buying";
    if (priceDiff > 0) return "Price above VWAP showing positive institutional sentiment";
    if (priceDiff > -2) return "Price near VWAP with neutral institutional activity";
    if (priceDiff > -5) return "Price below VWAP showing some institutional selling pressure";
    return "Price significantly below VWAP indicating strong institutional selling pressure";
  };

  const getVWAPStatusText = (vwapValue, currentPrice) => {
    if (!currentPrice || !vwapValue) return "Neutral";
    
    const priceDiff = ((currentPrice - vwapValue) / vwapValue) * 100;
    
    if (priceDiff > 2) return "Bullish";
    if (priceDiff > 0) return "Positive";
    if (priceDiff > -2) return "Neutral";
    if (priceDiff > -5) return "Bearish";
    return "Very Bearish";
  };

  const getVWAPColor = (vwapValue, currentPrice) => {
    if (!currentPrice || !vwapValue) return '#f59e0b'; // Orange for neutral
    
    const priceDiff = ((currentPrice - vwapValue) / vwapValue) * 100;
    
    if (priceDiff > 2) return '#22c55e'; // Green for bullish
    if (priceDiff > 0) return '#f59e0b'; // Orange for positive
    if (priceDiff > -2) return '#f59e0b'; // Orange for neutral
    if (priceDiff > -5) return '#ef4444'; // Red for bearish
    return '#ef4444'; // Red for very bearish
  };

  const getVWAPDeviationSummary = (vwapValue, currentPrice) => {
    if (!currentPrice || !vwapValue) return "Deviation data unavailable";
    
    const deviation = ((currentPrice - vwapValue) / vwapValue) * 100;
    
    if (deviation > 2) return "Significant positive deviation from VWAP";
    if (deviation > 0) return "Positive deviation from VWAP";
    if (deviation > -2) return "Minimal deviation from VWAP";
    if (deviation > -5) return "Negative deviation from VWAP";
    return "Significant negative deviation from VWAP";
  };

  const getVWAPDeviationStatus = (vwapValue, currentPrice) => {
    if (!currentPrice || !vwapValue) return "Neutral";
    
    const deviation = ((currentPrice - vwapValue) / vwapValue) * 100;
    
    if (deviation > 2) return "High";
    if (deviation > 0) return "Positive";
    if (deviation > -2) return "Low";
    if (deviation > -5) return "Negative";
    return "Very High";
  };

  const getVWAPDeviationColor = (vwapValue, currentPrice) => {
    if (!currentPrice || !vwapValue) return '#f59e0b'; // Orange for neutral
    
    const deviation = ((currentPrice - vwapValue) / vwapValue) * 100;
    
    if (deviation > 2) return '#22c55e'; // Green for high positive
    if (deviation > 0) return '#f59e0b'; // Orange for positive
    if (deviation > -2) return '#f59e0b'; // Orange for low
    if (deviation > -5) return '#ef4444'; // Red for negative
    return '#ef4444'; // Red for very high negative
  };

  // Helper functions for Bollinger Bands styling
  const getBollingerPositionValue = () => {
    if (!cryptoData?.price) return 50;
    
    const price = cryptoData.price;
    const upper = indicators.bollinger_bands?.upper || indicators.bollinger_upper;
    const lower = indicators.bollinger_bands?.lower || indicators.bollinger_lower;
    
    if (!upper || !lower) return 50;
    
    const position = ((price - lower) / (upper - lower)) * 100;
    return Math.max(0, Math.min(100, position));
  };

  const getBollingerStatus = () => {
    const position = getBollingerPositionValue();
    
    if (position > 80) return "Upper Band (Overbought)";
    if (position > 60) return "Upper Range";
    if (position > 40) return "Middle Range";
    if (position > 20) return "Lower Range";
    return "Lower Band (Oversold)";
  };

  const getBollingerColor = () => {
    const position = getBollingerPositionValue();
    
    if (position > 80) return '#ef4444'; // Red for overbought
    if (position > 60) return '#f59e0b'; // Orange for upper range
    if (position > 40) return '#3b82f6'; // Blue for middle range
    if (position > 20) return '#f59e0b'; // Orange for lower range
    return '#22c55e'; // Green for oversold
  };

  const getBollingerAnalysisText = () => {
    const position = getBollingerPositionValue();
    
    if (position > 80) return "Price is in the upper band, indicating potential overbought conditions";
    if (position > 60) return "Price is in the upper range, showing bullish momentum";
    if (position > 40) return "Price is in the middle range, indicating balanced market conditions";
    if (position > 20) return "Price is in the lower range, showing bearish momentum";
    return "Price is in the lower band, indicating potential oversold conditions";
  };

  const getMACDCrossoverAnalysis = () => {
    if (!indicators.macd || !indicators.macd.macd || !indicators.macd.signal) {
      return "MACD crossover data unavailable.";
    }
    
    const macd = indicators.macd.macd;
    const signal = indicators.macd.signal;
    const histogram = indicators.macd.histogram;
    
    if (macd > signal) {
      return `MACD (${macd.toFixed(4)}) above signal line (${signal.toFixed(4)}) — bullish momentum confirmed.`;
    } else {
      return `MACD (${macd.toFixed(4)}) below signal line (${signal.toFixed(4)}) — bearish momentum confirmed.`;
    }
  };

  const getEnhancedMACDAnalysis = () => {
    if (!indicators.macd || !indicators.macd.macd || !indicators.macd.signal) {
      return "MACD data unavailable — unable to assess trend momentum.";
    }
    
    const macd = indicators.macd.macd;
    const signal = indicators.macd.signal;
    const histogram = indicators.macd.histogram;
    
    // Check for crossover
    if (macd > signal) {
      return `MACD crossover confirmed: Line (${macd.toFixed(1)}) above Signal (${signal.toFixed(1)}) — bullish momentum accelerating.`;
    } else {
      return `MACD crossover confirmed: Line (${macd.toFixed(1)}) below Signal (${signal.toFixed(1)}) — bearish momentum accelerating.`;
    }
  };

  const getAIPoweredMACDAnalysis = async () => {
    if (!indicators.macd || !indicators.macd.macd || !indicators.macd.signal || !symbol) {
      return getMACDAnalysis(); // Fallback to regular analysis
    }
    
    try {
      // Create a focused MACD analysis request
      const macdData = {
        symbol: symbol,
        macd: indicators.macd.macd,
        signal: indicators.macd.signal,
        histogram: indicators.macd.histogram,
        price: cryptoData?.price,
        change_percent: cryptoData?.change_percent
      };
      
      const response = await axios.post(`${API}/cryptos/${symbol}/analyze`, {
        userProfile: userProfileData,
        focus: 'MACD',
        macdData: macdData
      });
      
      if (response.data && response.data.analysis) {
        // Extract MACD-specific insights from the AI analysis
        const analysis = response.data.analysis;
        if (analysis.includes('MACD') || analysis.includes('momentum')) {
          return analysis;
        } else {
          // If AI analysis doesn't focus on MACD, return enhanced local analysis
          return getEnhancedMACDAnalysis();
        }
      }
      
      return getEnhancedMACDAnalysis();
    } catch (error) {
      console.log('AI-powered MACD analysis not available, using enhanced local analysis');
      return getEnhancedMACDAnalysis();
    }
  };

  // New MACD Color Mapping Functions
  const getMACDValueColor = (value) => {
    if (value > 2.0) return '#22c55e'; // Strong bullish - Green
    if (value >= 0.5 && value <= 2.0) return '#22c55e'; // Mild bullish - Green
    if (value >= -0.5 && value < 0.5) return '#3b82f6'; // Neutral - Blue
    if (value >= -2.0 && value < -0.5) return '#f97316'; // Mild bearish - Orange
    return '#ef4444'; // Strong bearish - Red
  };

  const getHistogramColor = (value) => {
    if (value > 1.0) return '#22c55e'; // Strong bullish divergence - Green
    if (value >= 0.2 && value <= 1.0) return '#22c55e'; // Moderate bullish divergence - Green
    if (value >= -0.2 && value < 0.2) return '#3b82f6'; // Neutral - Blue
    if (value >= -1.0 && value < -0.2) return '#f97316'; // Moderate bearish divergence - Orange
    return '#ef4444'; // Strong bearish divergence - Red
  };

  const getMACDInterpretationColor = () => {
    if (!indicators.macd) return '#3b82f6'; // Default blue

    const { macd, signal, histogram } = indicators.macd;
    
    // Check for neutral state (all values close to 0 within ±0.01 threshold)
    const neutralThreshold = 0.01;
    const isNeutral = Math.abs(macd) <= neutralThreshold && 
                     Math.abs(signal) <= neutralThreshold && 
                     Math.abs(histogram) <= neutralThreshold;
    
    if (isNeutral) return '#3b82f6'; // Neutral blue
    
    // Strong Bullish (MACD above Signal and positive histogram)
    if (macd > signal && histogram > 0) {
      if (Math.abs(macd) > 2.0 && Math.abs(signal) > 2.0 && histogram > 1.0) {
        return '#22c55e'; // Strong bullish green
      } else {
        return '#22c55e'; // Mild bullish green
      }
    }
    
    // Strong Bearish (MACD below Signal and negative histogram)
    if (macd < signal && histogram < 0) {
      if (Math.abs(macd) > 2.0 && Math.abs(signal) > 2.0 && histogram < -1.0) {
        return '#ef4444'; // Strong bearish red
      } else {
        return '#f97316'; // Mild bearish orange
      }
    }
    
    // Slight bullish (MACD above Signal but weak histogram)
    if (macd > signal && Math.abs(histogram) <= 0.1) {
      return '#22c55e'; // Green for slight bullish
    }
    
    // Slight bearish (MACD below Signal but weak histogram)
    if (macd < signal && Math.abs(histogram) <= 0.1) {
      return '#f97316'; // Orange for slight bearish
    }
    
    // Default neutral
    return '#3b82f6';
  };

  const getMACDAnalysis = () => {
    if (!indicators.macd || !indicators.macd.macd || !indicators.macd.signal) {
      return "MACD data unavailable — unable to assess trend momentum.";
    }
    
    const macd = indicators.macd.macd;
    const signal = indicators.macd.signal;
    const histogram = indicators.macd.histogram;
    
    // Calculate the difference between MACD and Signal lines
    const macdSignalDiff = macd - signal;
    
    // Check for neutral state (all values close to 0 within ±0.01 threshold)
    const neutralThreshold = 0.01;
    const isNeutral = Math.abs(macd) <= neutralThreshold && 
                     Math.abs(signal) <= neutralThreshold && 
                     Math.abs(histogram) <= neutralThreshold;
    
    if (isNeutral) {
      return "Neutral momentum — MACD and signal lines aligned with flat histogram.";
    }
    
    // Five-tier momentum classification based on MACD-Signal relationship and histogram strength
    
    // 1. Strong Bullish Momentum
    if (macdSignalDiff > 0.10 && histogram > 0.05) {
      return "Strong bullish momentum — MACD line above signal line with positive histogram supporting continued upward movement.";
    }
    
    // 2. Slightly Bullish Momentum
    if (macdSignalDiff > 0.01 && macdSignalDiff <= 0.10 && histogram > 0.01 && histogram <= 0.05) {
      return "Slight bullish momentum — MACD line above signal line with mildly positive histogram.";
    }
    
    // 3. Neutral Momentum (MACD and Signal lines nearly equal)
    if (Math.abs(macdSignalDiff) <= 0.01 && Math.abs(histogram) <= 0.01) {
      return "Neutral momentum — MACD and signal lines aligned with flat histogram.";
    }
    
    // 4. Slightly Bearish Momentum
    if (macdSignalDiff < -0.01 && macdSignalDiff >= -0.10 && histogram < -0.01 && histogram >= -0.05) {
      return "Slight bearish momentum — MACD line below signal line with mildly negative histogram.";
    }
    
    // 5. Strong Bearish Momentum
    if (macdSignalDiff < -0.10 && histogram < -0.05) {
      return "Strong bearish momentum — MACD line below signal line with negative histogram supporting continued downward movement.";
    }
    
    // Fallback cases for edge scenarios
    if (macdSignalDiff > 0.01 && histogram > 0.01) {
      return "Slight bullish momentum — MACD line above signal line with mildly positive histogram.";
    }
    
    if (macdSignalDiff < -0.01 && histogram < -0.01) {
      return "Slight bearish momentum — MACD line below signal line with mildly negative histogram.";
    }
    
    // Mixed or uncertain signals
    return "MACD signals are mixed — momentum direction unclear, monitor for developing trends.";
  };

  const getBollingerAnalysis = () => {
    if (!indicators.bollinger_bands || !cryptoData?.price) {
      return "Bollinger Bands data unavailable — unable to assess volatility and price position.";
    }
    
    const price = cryptoData.price;
    const upper = indicators.bollinger_bands.upper;
    const middle = indicators.bollinger_bands.middle;
    const lower = indicators.bollinger_bands.lower;
    
    if (!upper || !middle || !lower) {
      return "Incomplete Bollinger Bands data — unable to determine price position.";
    }
    
    const upperDistance = ((upper - price) / price) * 100;
    const lowerDistance = ((price - lower) / price) * 100;
    
    if (price > upper) {
      return `Price at $${formatWithCommas(price, 2)} above upper band ($${formatWithCommas(upper, 2)}) — extreme overbought, high reversal risk.`;
          } else if (price < lower) {
        return `Price at $${formatWithCommas(price, 2)} below lower band ($${formatWithCommas(lower, 2)}) — extreme oversold, potential bounce opportunity.`;
          } else if (price > middle) {
        return `Price at $${formatWithCommas(price, 2)} above middle band ($${formatWithCommas(middle, 2)}) — upper half of volatility range.`;
          } else {
        return `Price at $${formatWithCommas(price, 2)} below middle band ($${formatWithCommas(middle, 2)}) — lower half of volatility range.`;
    }
  };

  const getPivotAnalysis = () => {
    if (!indicators.pivot_point || !cryptoData?.price) {
      return "Pivot point data unavailable — unable to assess support and resistance levels.";
    }
    
    const price = cryptoData.price;
    const pivot = indicators.pivot_point;
    const r1 = indicators.resistance_r1;
    const r2 = indicators.resistance_r2;
    const s1 = indicators.support_s1;
    const s2 = indicators.support_s2;
    
    if (!pivot || !r1 || !s1) {
      return "Incomplete pivot point data — unable to determine price position.";
    }
    
    // Calculate percentage distance from pivot
    const pivotDistance = Math.abs((price - pivot) / pivot) * 100;
    const isNearPivot = pivotDistance <= 0.2;
    
    let position, bias, nextLevel, nextResistance, nextSupport;
    
    if (price > r1) {
      // Price is above R1 - Strong Bullish
      return `Price is currently trading above R1, indicating strong bullish momentum, with R2 acting as the nearest key level and a potential breakout opening the path toward R3, while a pullback may retest R1.`;
    } else if (price > pivot && !isNearPivot) {
      // Price is between Pivot & R1 - Bullish Bias
      return `Price is currently trading between the Pivot Point and R1, indicating mild bullish bias, with R1 acting as the nearest key level and a potential breakout opening the path toward R2, while a pullback may retest the Pivot.`;
    } else if (isNearPivot) {
      // Price is very close to Pivot Point - Neutral/Indecisive
      if (price > pivot) {
        return `Price is currently trading near the Pivot Point (slightly above), indicating neutral bias with indecisive momentum, with R1 acting as the nearest key level and a potential breakout opening the path toward R2, while a breakdown may retest S1.`;
      } else {
        return `Price is currently trading near the Pivot Point (slightly below), indicating neutral bias with indecisive momentum, with S1 acting as the nearest key level and a potential breakdown opening the path toward S2, while a rebound may retest the Pivot.`;
      }
    } else if (price > s1) {
      // Price is between S1 & Pivot - Bearish Bias
      return `Price is currently trading between the Pivot Point and S1, indicating mild bearish bias, with S1 acting as the nearest key level and a potential breakdown opening the path toward S2, while a rebound may retest the Pivot.`;
    } else {
      // Price is below S1 - Strong Bearish
      return `Price is currently trading below S1, indicating strong bearish momentum, with S2 acting as the nearest key level and a potential breakdown opening the path toward S3, while a rebound may retest S1.`;
    }
  };

  const getPivotLevelColor = (levelType, levelValue) => {
    if (!indicators.pivot_point || !cryptoData?.price) {
      return { color: '#6b7280', opacity: 0.6, hasBorder: false, isDimmed: true }; // Default dimmed
    }
    
    const price = cryptoData.price;
    const pivot = indicators.pivot_point;
    const r1 = indicators.resistance_r1;
    const r2 = indicators.resistance_r2;
    const r3 = indicators.resistance_r3;
    const s1 = indicators.support_s1;
    const s2 = indicators.support_s2;
    const s3 = indicators.support_s3;
    
    if (!pivot || !r1 || !s1) {
      return { color: '#6b7280', opacity: 0.6, hasBorder: false, isDimmed: true };
    }
    
    // Determine market bias based on price vs pivot
    const isAbovePivot = price > pivot;
    
    // Apply new color mapping logic
    if (levelType === 'pivot') {
      // Pivot Point (P) - always blue, neutral anchor
      return { 
        color: '#3b82f6', 
        opacity: 1.0, 
        hasBorder: true, 
        isDimmed: false,
        borderColor: '#3b82f6',
        fontWeight: 'normal'
      };
    } else if (levelType === 's1') {
      // S1 - always red with red border
      return { 
        color: '#ef4444', 
        opacity: 1.0, 
        hasBorder: true, 
        isDimmed: false,
        borderColor: '#ef4444',
        fontWeight: 'normal'
      };
    } else if (levelType === 'r1') {
      // R1 - always green with green border
      return { 
        color: '#22c55e', 
        opacity: 1.0, 
        hasBorder: true, 
        isDimmed: false,
        borderColor: '#22c55e',
        fontWeight: 'normal'
      };
    } else if (levelType === 's2' && !isAbovePivot) {
      // S2 as forecast target when price is below pivot (bearish bias)
      return { 
        color: '#ef4444', 
        opacity: 1.0, 
        hasBorder: true, 
        isDimmed: false,
        isForecast: true,
        borderColor: '#ef4444',
        fontWeight: 'bold',
        fontSize: '1.1em' // Larger font size
      };
    } else if (levelType === 'r2' && isAbovePivot) {
      // R2 as forecast target when price is above pivot (bullish bias)
      return { 
        color: '#22c55e', 
        opacity: 1.0, 
        hasBorder: true, 
        isDimmed: false,
        isForecast: true,
        borderColor: '#22c55e',
        fontWeight: 'bold',
        fontSize: '1.1em' // Larger font size
      };
    } else {
      // All other levels (R3, S3, etc.) - dimmed
      return { color: '#6b7280', opacity: 0.8, hasBorder: false, isDimmed: true };
    }
  };

  const getATRAnalysis = () => {
    if (!indicators.atr || !cryptoData?.price) {
      return "ATR data unavailable — unable to assess volatility levels.";
    }
    
    const atr = indicators.atr;
    const price = cryptoData.price;
    const volatilityPercent = (atr / price) * 100;
    
    if (volatilityPercent > 5) {
      return `High volatility detected — ATR of $${atr.toFixed(2)} represents ${volatilityPercent.toFixed(1)}% of price, expect wide price swings.`;
    } else if (volatilityPercent > 2) {
      return `Moderate volatility — ATR of $${atr.toFixed(2)} represents ${volatilityPercent.toFixed(1)}% of price, normal trading conditions.`;
    } else {
      return `Low volatility — ATR of $${atr.toFixed(2)} represents ${volatilityPercent.toFixed(1)}% of price, expect tight price action.`;
    }
  };

  const getATRStopLossLevels = () => {
    if (!indicators.atr || !cryptoData?.price) {
      return { long: null, short: null };
    }
    
    const atr = indicators.atr;
    const price = cryptoData.price;
    
    return {
      long: price - (atr * 2),
      short: price + (atr * 2)
    };
  };

  const getATRVolatilityLevel = () => {
    if (!indicators.atr || !cryptoData?.price) return 'unknown';
    
    const atr = indicators.atr;
    const price = cryptoData.price;
    const volatilityPercent = (atr / price) * 100;
    
    if (volatilityPercent > 5) return 'high';
    if (volatilityPercent > 2) return 'medium';
    return 'low';
  };

  const getATRPercentageLevel = () => {
    if (!indicators.atr_percentage) return 'moderate';
    
    const percentage = indicators.atr_percentage;
    
    if (percentage >= 0.0 && percentage < 1.5) return 'low';
    if (percentage >= 1.5 && percentage < 3.0) return 'mild';
    if (percentage >= 3.0 && percentage < 4.5) return 'moderate';
    if (percentage >= 4.5 && percentage < 6.5) return 'elevated';
    if (percentage >= 6.5 && percentage < 9.0) return 'high';
    return 'extreme';
  };

  const getATRPercentageColor = () => {
    if (!indicators.atr_percentage) return '#f59e0b'; // Default yellow
    
    const percentage = indicators.atr_percentage;
    
    if (percentage >= 0.0 && percentage < 1.5) return '#22c55e'; // Green
    if (percentage >= 1.5 && percentage < 3.0) return '#22c55e'; // Green
    if (percentage >= 3.0 && percentage < 4.5) return '#f59e0b'; // Yellow
    if (percentage >= 4.5 && percentage < 6.5) return '#f97316'; // Orange
    if (percentage >= 6.5 && percentage < 9.0) return '#ef4444'; // Red
    return '#a855f7'; // Purple
  };

  const getVWAPAnalysis = () => {
    if (!indicators.vwap || !cryptoData?.price) {
      return "VWAP data unavailable — unable to assess institutional buying pressure.";
    }
    
    const price = cryptoData.price;
    const vwap = indicators.vwap;
    const deviation = ((price - vwap) / vwap) * 100;
    
    if (deviation > 15) {
      return `Price at $${formatWithCommas(price, 2)} significantly above VWAP ($${formatWithCommas(vwap, 2)}) by ${deviation.toFixed(1)}% — strong institutional buying pressure.`;
          } else if (deviation > 5) {
        return `Price at $${formatWithCommas(price, 2)} above VWAP ($${formatWithCommas(vwap, 2)}) by ${deviation.toFixed(1)}% — moderate bullish pressure.`;
          } else if (deviation < -15) {
        return `Price at $${formatWithCommas(price, 2)} significantly below VWAP ($${formatWithCommas(vwap, 2)}) by ${Math.abs(deviation).toFixed(1)}% — strong institutional selling pressure.`;
          } else if (deviation < -5) {
        return `Price at $${formatWithCommas(price, 2)} below VWAP ($${formatWithCommas(vwap, 2)}) by ${Math.abs(deviation).toFixed(1)}% — moderate bearish pressure.`;
          } else {
        return `Price at $${formatWithCommas(price, 2)} near VWAP ($${formatWithCommas(vwap, 2)}) — balanced institutional activity.`;
    }
  };

  const getVWAPStatus = () => {
    if (!indicators.vwap || !cryptoData?.price) return 'unknown';
    
    const price = cryptoData.price;
    const vwap = indicators.vwap;
    
    if (price > vwap) return 'above';
    if (price < vwap) return 'below';
    return 'at';
  };

  const getMovingAveragesAnalysis = () => {
    if (!indicators.ema_12 || !indicators.ema_26 || !indicators.sma_50 || !indicators.sma_200 || !cryptoData?.price) {
      return "Moving average data unavailable — unable to assess trend alignment.";
    }
    
    const currentPrice = cryptoData.price;
    const ema12 = indicators.ema_12;
    const ema26 = indicators.ema_26;
    const sma50 = indicators.sma_50;
    const sma200 = indicators.sma_200;
    
    // Helper function to classify price position relative to MA
    const classifyPosition = (price, ma) => {
      const percentDiff = ((price - ma) / ma) * 100;
      if (percentDiff > 1) return "above";
      if (percentDiff < -1) return "below";
      return "near";
    };
    
    // Classify each MA
    const ema12Position = classifyPosition(currentPrice, ema12);
    const ema26Position = classifyPosition(currentPrice, ema26);
    const sma50Position = classifyPosition(currentPrice, sma50);
    const sma200Position = classifyPosition(currentPrice, sma200);
    
    // Count classifications
    const positions = [ema12Position, ema26Position, sma50Position, sma200Position];
    const aboveCount = positions.filter(pos => pos === "above").length;
    const belowCount = positions.filter(pos => pos === "below").length;
    const nearCount = positions.filter(pos => pos === "near").length;
    
    // Determine short-term and long-term classifications
    const shortTermPositions = [ema12Position, ema26Position];
    const longTermPositions = [sma50Position, sma200Position];
    const shortTermBelow = shortTermPositions.filter(pos => pos === "below").length;
    const shortTermNear = shortTermPositions.filter(pos => pos === "near").length;
    const shortTermAbove = shortTermPositions.filter(pos => pos === "above").length;
    const longTermBelow = longTermPositions.filter(pos => pos === "below").length;
    const longTermAbove = longTermPositions.filter(pos => pos === "above").length;
    const longTermNear = longTermPositions.filter(pos => pos === "near").length;
    
    // Generate smart summary based on structure analysis
    if (nearCount >= 3) {
      return "All MAs are tightly clustered, showing a neutral structure with potential for breakout in either direction.";
    } else if (belowCount >= 3) {
      return "Price is below all key MAs, confirming strong bearish momentum across timeframes.";
    } else if (aboveCount >= 3) {
      return "Price is above all key MAs, confirming strong bullish momentum across timeframes.";
    } else if (shortTermBelow >= 2 && longTermNear >= 1) {
      return "Short-term MAs are turning lower while long-term MAs remain steady, hinting at pressure building for a larger move.";
    } else if (shortTermNear >= 2 && longTermAbove >= 1) {
      return "Long-term MAs maintain bullish structure despite short-term pause, suggesting trend continuation if support holds.";
    } else if (shortTermBelow >= 2 && longTermAbove >= 1) {
      return "The price is sandwiched between short-term and long-term MAs, reflecting indecision and likely consolidation.";
    } else if (shortTermAbove >= 2 && longTermBelow >= 1) {
      return "Short-term momentum is bullish but long-term structure remains weak, indicating potential reversal or limited upside.";
    } else if (shortTermBelow >= 2 && longTermBelow >= 2) {
      return "Both short-term and long-term MAs confirm bearish alignment, suggesting sustained downward pressure.";
    } else if (shortTermAbove >= 2 && longTermAbove >= 2) {
      return "Both short-term and long-term MAs confirm bullish alignment, suggesting sustained upward momentum.";
    } else {
      return "Conflicting signals across timeframes suggest uncertain momentum with no clear directional bias.";
    }
  };

  const getBollingerPosition = () => {
    if (!indicators.bollinger_bands || !cryptoData?.price) return 50;
    
    const price = cryptoData.price;
    const upper = indicators.bollinger_bands.upper;
    const lower = indicators.bollinger_bands.lower;
    
    if (!upper || !lower) return 50;
    
    const position = ((price - lower) / (upper - lower)) * 100;
    return Math.max(0, Math.min(100, position));
  };

  const getKeyInsights = () => {
    const insights = [];
    const { ema_12, ema_26, rsi, adx, sma_20 } = indicators;
    const currentPrice = cryptoData?.price;
    
    if (ema_12 && ema_26) {
      if (ema_12 > ema_26) {
        insights.push("• EMA(12) > EMA(26): short-term bullish");
      } else {
        insights.push("• EMA(12) < EMA(26): short-term bearish");
      }
    }
    
    if (rsi) {
      if (rsi > 70) {
        insights.push("• RSI overbought: potential reversal risk");
      } else if (rsi < 30) {
        insights.push("• RSI oversold: potential bounce opportunity");
      } else {
        insights.push("• RSI in neutral zone: no overbought pressure");
      }
    }
    
    if (adx) {
      if (adx < 20) {
        insights.push("• ADX indicates unclear trend");
      } else {
        insights.push("• ADX shows strong trending market");
      }
    }
    
    if (sma_20 && currentPrice) {
      const diff = Math.abs(currentPrice - sma_20);
      const percentDiff = (diff / sma_20) * 100;
      if (percentDiff < 2) {
        insights.push("• Price near SMA(20): expect chop or consolidation");
      }
    }
    
    return insights;
  };

  const getAISummary = async () => {
    if (!indicators || !cryptoData?.price) {
      return "Insufficient data available for AI analysis.";
    }

    try {
      const prompt = `You are a financial analyst writing an expert summary for a crypto insights panel.

Using the following live indicator data:
- MACD (Line: ${indicators.macd?.macd?.toFixed(4) || 'N/A'}, Signal: ${indicators.macd?.signal?.toFixed(4) || 'N/A'}, Histogram: ${indicators.macd?.histogram?.toFixed(4) || 'N/A'})
- RSI: ${indicators.rsi?.toFixed(2) || 'N/A'} (${indicators.rsi > 70 ? 'Overbought' : indicators.rsi < 30 ? 'Oversold' : 'Neutral'})
- Momentum Indicators: CCI ${indicators.cci?.toFixed(2) || 'N/A'}, Stochastic %K ${indicators.stochastic_k?.toFixed(2) || 'N/A'}, Williams %R ${indicators.williams_r?.toFixed(2) || 'N/A'}
- Moving Averages: EMA(12) ${indicators.ema_12?.toFixed(2) || 'N/A'}, EMA(26) ${indicators.ema_26?.toFixed(2) || 'N/A'}, SMA(50) ${indicators.sma_50?.toFixed(2) || 'N/A'}, SMA(200) ${indicators.sma_200?.toFixed(2) || 'N/A'}
- VWAP: ${indicators.vwap?.toFixed(2) || 'N/A'}
- Bollinger Bands: Upper ${indicators.bollinger_bands?.upper?.toFixed(2) || 'N/A'}, Middle ${indicators.bollinger_bands?.middle?.toFixed(2) || 'N/A'}, Lower ${indicators.bollinger_bands?.lower?.toFixed(2) || 'N/A'}
- ATR: ${indicators.atr?.toFixed(2) || 'N/A'} (${indicators.atr_percentage?.toFixed(1) || 'N/A'}% of price)
- Pivot Levels: Pivot ${indicators.pivot_point?.toFixed(2) || 'N/A'}, R1 ${indicators.resistance_r1?.toFixed(2) || 'N/A'}, S1 ${indicators.support_s1?.toFixed(2) || 'N/A'}
- Current Price: $${formatWithCommas(cryptoData.price, 2)}

Generate a **clear, professional, educational summary** that:
- Explains the current market momentum and volatility
- Detects whether signals are aligned or conflicting
- Indicates whether overall sentiment is bullish, bearish, or neutral
- References specific indicators where necessary (e.g., "MACD divergence signals...", or "RSI indicates short-term oversold...")
- Offers short-term outlook (next 1–3 days) based on combined data
- Avoids financial advice language (e.g., "Buy" or "Sell") but stays informative and insightful
- Educates the reader slightly on why certain indicators matter (in 1–2 sentences max)

The tone should be confident, intelligent, and suitable for both beginner and experienced traders. Keep it under **200 words**."`;

      const response = await fetch(`${API}/analyze-crypto`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          symbol: symbol,
          focus: 'AI_SUMMARY',
          prompt: prompt
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch AI analysis');
      }

      const data = await response.json();
      return data.analysis || generateFallbackAnalysis();
    } catch (error) {
      console.error('Error fetching AI summary:', error);
      return generateFallbackAnalysis();
    }
  };

  const generateFallbackAnalysis = () => {
    if (!indicators || !cryptoData?.price) {
      return "Insufficient data available for AI analysis.";
    }

    const currentPrice = cryptoData.price;
    const priceChange = cryptoData.change_percent || 0;
    const rsi = indicators.rsi;
    const macd = indicators.macd;
    const vwap = indicators.vwap;
    
    let sentiment = "neutral";
    let momentum = "sideways";
    let volatility = "moderate";
    
    // Determine sentiment based on RSI
    if (rsi > 70) sentiment = "bearish (overbought)";
    else if (rsi < 30) sentiment = "bullish (oversold)";
    else if (rsi > 50) sentiment = "slightly bullish";
    else sentiment = "slightly bearish";
    
    // Determine momentum based on MACD
    if (macd?.macd && macd?.signal) {
      if (macd.macd > macd.signal && macd.histogram > 0) momentum = "bullish";
      else if (macd.macd < macd.signal && macd.histogram < 0) momentum = "bearish";
    }
    
    // Determine volatility based on ATR percentage
    if (indicators.atr_percentage) {
      if (indicators.atr_percentage > 4) volatility = "high";
      else if (indicators.atr_percentage < 1) volatility = "low";
    }
    
    return `Market analysis shows ${cryptoName || symbol} trading at $${formatWithCommas(currentPrice, 2)} with ${priceChange >= 0 ? 'positive' : 'negative'} momentum. RSI indicates ${sentiment} conditions, while MACD suggests ${momentum} momentum. Volatility appears ${volatility} based on current price action. Monitor key support and resistance levels for potential trading opportunities.`;
  };

  const bias = getBias();
  const bollingerPosition = getBollingerPosition();
  const keyInsights = getKeyInsights();
  const bollingerAnalysis = getBollingerAnalysis();
  const movingAveragesAnalysis = getMovingAveragesAnalysis();
  const momentumAnalysis = getMomentumAnalysis();
  const macdAnalysis = getMACDAnalysis();
  const macdCrossoverAnalysis = getMACDCrossoverAnalysis();
  const rsiAnalysis = getRSIAnalysis();
  const vwapAnalysis = getVWAPAnalysis();
  const vwapStatus = getVWAPStatus();
  const atrAnalysis = getATRAnalysis();
  const atrStopLoss = getATRStopLossLevels();
  const atrVolatilityLevel = getATRVolatilityLevel();

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="asset-insight-panel" onClick={(e) => e.stopPropagation()}>
        <div className="panel-header">
          <div className="panel-header-left">
            <div className="modal-logo">
              {profileData?.image ? (
                <img 
                  src={profileData.image}
                  alt={`${cryptoName} logo`}
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%'
                  }}
                />
              ) : (
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: '#f3f4f6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  color: '#6b7280'
                }}>
                  {/* Show first letter of crypto name if no logo */}
                  {cryptoName?.charAt(0) || symbol?.charAt(0) || '?'}
                </div>
              )}
            </div>
            <h2 className="panel-title">{cryptoName} ({symbol})</h2>
          </div>
          <div className="panel-header-right">
            <div className="live-price-container">
              {cryptoData?.price ? (
                <>
                  <span className="live-price">$<FormattedPrice priceData={formatCryptoPrice(cryptoData.price)} /></span>
                  <span className={`live-change ${cryptoData.change >= 0 ? 'positive' : 'negative'}`}>
                    {formatPercentageChange(cryptoData.change_percent)}
                  </span>
                </>
              ) : (
                <div className="price-loading-container">
                  <div className="price-loading-spinner">
                    <svg width="24" height="24" viewBox="0 0 24 24" className="spinner-svg">
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="2"
                        fill="none"
                        strokeLinecap="round"
                        strokeDasharray="62.83"
                        strokeDashoffset="62.83"
                        className="spinner-circle"
                      />
                    </svg>
                  </div>
                  <span className="price-loading-text">Loading...</span>
                </div>
              )}
            </div>
            <button className="panel-close" onClick={onClose}>×</button>
          </div>
        </div>
        
        <div className="panel-content">
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner-modal"></div>
              <p className="loading-text">{currentLoadingPhrase}</p>
            </div>
          ) : (
            <>


              {/* MACD INDICATOR Section - MOVED TO TOP */}
              {indicators.macd && (
                <div className="indicator-section">
                  <h3 className="section-title">MACD Indicator</h3>
                  <div className="indicator-card">
                    <div className="macd-grid">
                      <div className="macd-item">
                        <div className="macd-label-container">
                          <span className="macd-label">MACD Line</span>
                          <span className="macd-helper">Trend momentum</span>
                        </div>
                        <div className="macd-value-container">
                          <span className="macd-value" style={{ 
                            color: getMACDValueColor(indicators.macd.macd),
                            borderColor: getMACDValueColor(indicators.macd.macd),
                            boxShadow: `0 0 6px ${getMACDValueColor(indicators.macd.macd)}40`
                          }}>{formatIndicatorValue(indicators.macd.macd)}</span>
                          {indicators.macd.macd > indicators.macd.signal ? (
                            <span className="macd-crossover-indicator bullish">↗</span>
                          ) : (
                            <span className="macd-crossover-indicator bearish">↘</span>
                          )}
                        </div>
                      </div>
                      <div className="macd-item">
                        <div className="macd-label-container">
                          <span className="macd-label">Signal Line</span>
                          <span className="macd-helper">Trigger for momentum shift</span>
                        </div>
                        <div className="macd-value-container">
                          <span className="macd-value" style={{ 
                            color: getMACDValueColor(indicators.macd.signal),
                            borderColor: getMACDValueColor(indicators.macd.signal),
                            boxShadow: `0 0 6px ${getMACDValueColor(indicators.macd.signal)}40`
                          }}>{formatIndicatorValue(indicators.macd.signal)}</span>
                        </div>
                      </div>
                      <div className="macd-item">
                        <div className="macd-label-container">
                          <span className="macd-label">Histogram</span>
                          <span className="macd-helper">MACD vs Signal</span>
                        </div>
                        <div className="macd-value-container">
                          <span className="macd-value" style={{ 
                            color: getHistogramColor(indicators.macd.histogram),
                            borderColor: getHistogramColor(indicators.macd.histogram),
                            boxShadow: `0 0 6px ${getHistogramColor(indicators.macd.histogram)}40`
                          }}>{formatIndicatorValue(indicators.macd.histogram)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="indicator-summary" style={{
                      borderColor: getMACDInterpretationColor(),
                      boxShadow: `0 2px 4px ${getMACDInterpretationColor()}40`
                    }}>
                      {macdAnalysis}
                    </div>
                  </div>
                </div>
              )}

              {/* RSI INDICATOR Section */}
              {(indicators.rsi_7 || indicators.rsi_14 || indicators.rsi_21) && (
                <div className="indicator-section">
                  <h3 className="section-title">RELATIVE STRENGTH INDEX (RSI)</h3>
                  <div className="rsi-panels">
                    {indicators.rsi_7 && (
                      <div className="rsi-panel">
                        <div className="rsi-panel-header">
                          <span className="rsi-period">RSI (7)</span>
                          <div className="rsi-value-pill" style={{ 
                            color: getRSIColor(indicators.rsi_7)
                          }}>
                            {indicators.rsi_7.toFixed(2)}
                          </div>
                        </div>
                        <div className="rsi-status-line">
                          <span className="rsi-summary-left">
                            {getRSISummary(7)}
                          </span>
                          <span className="rsi-status-right" style={{ 
                            color: getRSIColor(indicators.rsi_7)
                          }}>
                            {getRSILabel(indicators.rsi_7)}
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {indicators.rsi_14 && (
                      <div className="rsi-panel">
                        <div className="rsi-panel-header">
                          <span className="rsi-period">RSI (14)</span>
                          <div className="rsi-value-pill" style={{ 
                            color: getRSIColor(indicators.rsi_14)
                          }}>
                            {indicators.rsi_14.toFixed(2)}
                          </div>
                        </div>
                        <div className="rsi-status-line">
                          <span className="rsi-summary-left">
                            {getRSISummary(14)}
                          </span>
                          <span className="rsi-status-right" style={{ 
                            color: getRSIColor(indicators.rsi_14)
                          }}>
                            {getRSILabel(indicators.rsi_14)}
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {indicators.rsi_21 && (
                      <div className="rsi-panel">
                        <div className="rsi-panel-header">
                          <span className="rsi-period">RSI (21)</span>
                          <div className="rsi-value-pill" style={{ 
                            color: getRSIColor(indicators.rsi_21)
                          }}>
                            {indicators.rsi_21.toFixed(2)}
                          </div>
                        </div>
                        <div className="rsi-status-line">
                          <span className="rsi-summary-left">
                            {getRSISummary(21)}
                          </span>
                          <span className="rsi-status-right" style={{ 
                            color: getRSIColor(indicators.rsi_21)
                          }}>
                            {getRSILabel(indicators.rsi_21)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="indicator-summary" style={{
                    borderColor: getRSISummaryBorderColor(),
                    boxShadow: `0 2px 4px ${getRSISummaryBorderColor()}40`
                  }}>
                    {getRSISummaryAnalysis()}
                  </div>
                </div>
              )}

              {/* MOMENTUM INDICATORS Section */}
              {(indicators.stochastic_k || indicators.stochastic_d || indicators.williams_r || indicators.cci) && (
                <div className="indicator-section">
                  <h3 className="section-title">MOMENTUM INDICATORS</h3>
                  <div className="momentum-panels">
                    {indicators.stochastic_k && (
                      <div className="momentum-panel">
                        <div className="momentum-panel-header">
                          <span className="momentum-period">Stochastic %K</span>
                          <div className="momentum-value-pill" style={{ 
                            color: getMomentumColor('stochastic', indicators.stochastic_k)
                          }}>
                            {indicators.stochastic_k.toFixed(2)}
                          </div>
                        </div>
                        <div className="momentum-status-line">
                          <span className="momentum-summary-left">
                            {getStochasticSummary(indicators.stochastic_k)}
                          </span>
                          <span className="momentum-status-right" style={{ 
                            color: getMomentumColor('stochastic', indicators.stochastic_k)
                          }}>
                            {getStochasticStatus(indicators.stochastic_k)}
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {indicators.stochastic_d && (
                      <div className="momentum-panel">
                        <div className="momentum-panel-header">
                          <span className="momentum-period">Stochastic %D</span>
                          <div className="momentum-value-pill" style={{ 
                            color: getMomentumColor('stochastic', indicators.stochastic_d)
                          }}>
                            {indicators.stochastic_d.toFixed(2)}
                          </div>
                        </div>
                        <div className="momentum-status-line">
                          <span className="momentum-summary-left">
                            Signal line indicator
                          </span>
                          <span className="momentum-status-right" style={{ 
                            color: getMomentumColor('stochastic', indicators.stochastic_d)
                          }}>
                            {getStochasticStatus(indicators.stochastic_d)}
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {indicators.williams_r && (
                      <div className="momentum-panel">
                        <div className="momentum-panel-header">
                          <span className="momentum-period">Williams %R</span>
                          <div className="momentum-value-pill" style={{ 
                            color: getMomentumColor('williams', indicators.williams_r)
                          }}>
                            {indicators.williams_r.toFixed(2)}
                          </div>
                        </div>
                        <div className="momentum-status-line">
                          <span className="momentum-summary-left">
                            {getWilliamsSummary(indicators.williams_r)}
                          </span>
                          <span className="momentum-status-right" style={{ 
                            color: getMomentumColor('williams', indicators.williams_r)
                          }}>
                            {getWilliamsStatus(indicators.williams_r)}
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {indicators.cci && (
                      <div className="momentum-panel">
                        <div className="momentum-panel-header">
                          <span className="momentum-period">CCI (Commodity Channel Index)</span>
                          <div className="momentum-value-pill" style={{ 
                            color: getMomentumColor('cci', indicators.cci)
                          }}>
                            {indicators.cci.toFixed(2)}
                          </div>
                        </div>
                        <div className="momentum-status-line">
                          <span className="momentum-summary-left">
                            {getCCISummary(indicators.cci)}
                          </span>
                          <span className="momentum-status-right" style={{ 
                            color: getMomentumColor('cci', indicators.cci)
                          }}>
                            {getCCIStatus(indicators.cci)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="indicator-summary" style={{
                    borderColor: getMomentumSummaryBorderColor(),
                    boxShadow: `0 2px 4px ${getMomentumSummaryBorderColor()}40`
                  }}>
                    {getMomentumAnalysis()}
                  </div>
                </div>
              )}

              {/* MOVING AVERAGES Section */}
              {(indicators.ema_12 || indicators.ema_26 || indicators.sma_50 || indicators.sma_200 || indicators.sma_20) && (
                <div className="indicator-section">
                  <h3 className="section-title">MOVING AVERAGES</h3>
                  <div className="ma-panels">
                    {indicators.ema_12 && (
                      <div className="ma-panel">
                        <div className="ma-panel-header">
                          <span className="ma-period">EMA(12)</span>
                          <div className="ma-value-pill" style={{ 
                            color: getMAColor('ema_12', indicators.ema_12, cryptoData?.price)
                          }}>
                            ${formatWithCommas(indicators.ema_12, 2)}
                          </div>
                        </div>
                        <div className="ma-status-line">
                          <span className="ma-summary-left">
                            {getMASummary('ema_12', indicators.ema_12, cryptoData?.price)}
                          </span>
                          <span className="ma-status-right" style={{ 
                            color: getMAColor('ema_12', indicators.ema_12, cryptoData?.price)
                          }}>
                            {getMAStatus('ema_12', indicators.ema_12, cryptoData?.price)}
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {indicators.ema_26 && (
                      <div className="ma-panel">
                        <div className="ma-panel-header">
                          <span className="ma-period">EMA(26)</span>
                          <div className="ma-value-pill" style={{ 
                            color: getMAColor('ema_26', indicators.ema_26, cryptoData?.price)
                          }}>
                            ${formatWithCommas(indicators.ema_26, 2)}
                          </div>
                        </div>
                        <div className="ma-status-line">
                          <span className="ma-summary-left">
                            {getMASummary('ema_26', indicators.ema_26, cryptoData?.price)}
                          </span>
                          <span className="ma-status-right" style={{ 
                            color: getMAColor('ema_26', indicators.ema_26, cryptoData?.price)
                          }}>
                            {getMAStatus('ema_26', indicators.ema_26, cryptoData?.price)}
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {indicators.sma_50 && (
                      <div className="ma-panel">
                        <div className="ma-panel-header">
                          <span className="ma-period">SMA(50)</span>
                          <div className="ma-value-pill" style={{ 
                            color: getMAColor('sma_50', indicators.sma_50, cryptoData?.price)
                          }}>
                            ${formatWithCommas(indicators.sma_50, 2)}
                          </div>
                        </div>
                        <div className="ma-status-line">
                          <span className="ma-summary-left">
                            {getMASummary('sma_50', indicators.sma_50, cryptoData?.price)}
                          </span>
                          <span className="ma-status-right" style={{ 
                            color: getMAColor('sma_50', indicators.sma_50, cryptoData?.price)
                          }}>
                            {getMAStatus('sma_50', indicators.sma_50, cryptoData?.price)}
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {(() => {
                      console.log('SMA 200 Debug:', {
                        sma_200: indicators.sma_200,
                        has_sma_200: !!indicators.sma_200,
                        all_indicators: indicators
                      });
                      return indicators.sma_200;
                    })() && (
                      <div className="ma-panel">
                        <div className="ma-panel-header">
                          <span className="ma-period">SMA(200)</span>
                          <div className="ma-value-pill" style={{ 
                            color: getMAColor('sma_200', indicators.sma_200, cryptoData?.price)
                          }}>
                            ${formatWithCommas(indicators.sma_200, 2)}
                          </div>
                        </div>
                        <div className="ma-status-line">
                          <span className="ma-summary-left">
                            {getMASummary('sma_200', indicators.sma_200, cryptoData?.price)}
                          </span>
                          <span className="ma-status-right" style={{ 
                            color: getMAColor('sma_200', indicators.sma_200, cryptoData?.price)
                          }}>
                            {getMAStatus('sma_200', indicators.sma_200, cryptoData?.price)}
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {!indicators.sma_200 && indicators.sma_20 && (
                      <div className="ma-panel">
                        <div className="ma-panel-header">
                          <span className="ma-period">SMA(20)</span>
                          <div className="ma-value-pill" style={{ 
                            color: getMAColor('sma_20', indicators.sma_20, cryptoData?.price)
                          }}>
                            ${formatWithCommas(indicators.sma_20, 2)}
                          </div>
                        </div>
                        <div className="ma-status-line">
                          <span className="ma-summary-left">
                            {getMASummary('sma_20', indicators.sma_20, cryptoData?.price)}
                          </span>
                          <span className="ma-status-right" style={{ 
                            color: getMAColor('sma_20', indicators.sma_20, cryptoData?.price)
                          }}>
                            {getMAStatus('sma_20', indicators.sma_20, cryptoData?.price)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="indicator-summary">
                    {getMovingAveragesAnalysis()}
                  </div>
                </div>
              )}

              {/* VWAP Section */}
              {indicators.vwap && (
                <div className="indicator-section">
                  <h3 className="section-title">VWAP (VOLUME WEIGHTED AVERAGE PRICE)</h3>
                  <div className="vwap-structured-layout">
                    {/* Current Price vs VWAP Section */}
                    <div className="vwap-comparison-section">
                      <div className="comparison-header">VWAP vs Current Price</div>
                      <div className="comparison-content">
                        <div className="vwap-display">
                          <div className="vwap-value-pill" style={{ 
                            color: getVWAPDeviationColor(indicators.vwap, cryptoData?.price)
                          }}>
                            ${formatWithCommas(indicators.vwap, 2)}
                          </div>
                        </div>
                        <div className="vs-separator">VS</div>
                        <div className="current-price-display">
                          <span className="current-price-text">${formatWithCommas(cryptoData?.price, 2)}</span>
                        </div>
                      </div>
                      
                      {/* Deviation from VWAP - Now inside the comparison section */}
                      <div className="vwap-deviation-inline">
                        <div className="deviation-header">Deviation from VWAP</div>
                        <div className="deviation-value-display" style={{ 
                          color: getVWAPDeviationColor(indicators.vwap, cryptoData?.price)
                        }}>
                          {cryptoData?.price && indicators.vwap ? 
                            `${((cryptoData.price - indicators.vwap) / indicators.vwap * 100) > 0 ? '+' : ''}${((cryptoData.price - indicators.vwap) / indicators.vwap * 100).toFixed(2)}%` : 
                            'N/A'
                          }
                        </div>
                      </div>
                      

                    </div>

                    {/* Summary Section */}
                    <div className="vwap-summary-section" style={{
                      borderColor: getVWAPDeviationColor(indicators.vwap, cryptoData?.price),
                      boxShadow: `0 2px 4px ${getVWAPDeviationColor(indicators.vwap, cryptoData?.price)}40`
                    }}>
                      <div className="summary-icon">📈</div>
                      <div className="summary-text">
                        {getVWAPAnalysis()}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* VOLATILITY & BOLLINGER BANDS Section */}
              {(() => {
                console.log('Bollinger Bands Debug:', {
                  bollinger_bands: indicators.bollinger_bands,
                  bollinger_upper: indicators.bollinger_upper,
                  bollinger_middle: indicators.bollinger_middle,
                  bollinger_lower: indicators.bollinger_lower
                });
                return (indicators.bollinger_bands || indicators.bollinger_upper || indicators.bollinger_middle || indicators.bollinger_lower);
              })() && (
                <div className="indicator-section">
                  <h3 className="section-title">VOLATILITY & BOLLINGER BANDS</h3>
                  <div className="bollinger-structured-layout">
                    <div className="bollinger-panels">
                      {/* Bollinger Upper Band */}
                      <div className="bollinger-panel">
                        <div className="bollinger-panel-header">
                          <span className="bollinger-period">Bollinger Upper</span>
                          <div className="bollinger-value-pill" style={{ 
                            color: '#3b82f6'
                          }}>
                            ${formatWithCommas(indicators.bollinger_bands?.upper || indicators.bollinger_upper || 0, 2)}
                          </div>
                        </div>
                      </div>

                      {/* Bollinger Middle Band */}
                      <div className="bollinger-panel">
                        <div className="bollinger-panel-header">
                          <span className="bollinger-period">Bollinger Middle</span>
                          <div className="bollinger-value-pill" style={{ 
                            color: '#3b82f6'
                          }}>
                            ${formatWithCommas(indicators.bollinger_bands?.middle || indicators.bollinger_middle || 0, 2)}
                          </div>
                        </div>
                      </div>

                      {/* Bollinger Lower Band */}
                      <div className="bollinger-panel">
                        <div className="bollinger-panel-header">
                          <span className="bollinger-period">Bollinger Lower</span>
                          <div className="bollinger-value-pill" style={{ 
                            color: '#3b82f6'
                          }}>
                            ${formatWithCommas(indicators.bollinger_bands?.lower || indicators.bollinger_lower || 0, 2)}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Current Price Position Bar */}
                    <div className="price-position-section">
                      <div className="position-label">Current Price Position</div>
                      <div className="position-bar-container">
                        <div className="position-bar">
                          <div 
                            className="position-indicator" 
                            style={{ 
                              left: `${getBollingerPositionValue()}%`,
                              backgroundColor: getBollingerColor()
                            }}
                          ></div>
                        </div>
                      </div>
                      <div className="position-status" style={{ 
                        color: getBollingerColor()
                      }}>
                        {getBollingerStatus()}
                      </div>
                      
                      {/* Analysis Summary - Now inside the price position section */}
                      <div className="indicator-summary" style={{
                        borderColor: getBollingerColor(),
                        boxShadow: `0 2px 4px ${getBollingerColor()}40`,
                        marginTop: '12px'
                      }}>
                        {getBollingerAnalysisText()}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ATR Section */}
              {indicators.atr && (
                <div className="indicator-section">
                  <h3 className="section-title">ATR (Average True Range)</h3>
                  
                  {/* ATR Value Card */}
                  <div className="atr-card">
                    <div className="atr-header">
                      <span className="atr-label">ATR Value:</span>
                      <div className={`atr-value-pill ${getATRPercentageLevel()}`}>
                        ${formatIndicatorValue(indicators.atr)}
                      </div>
                    </div>
                  </div>

                  {/* ATR Percentage Card */}
                  {indicators.atr_percentage && (
                    <div className="atr-card">
                      <div className="atr-header">
                        <span className="atr-label">% of Price:</span>
                        <div className={`atr-percentage-pill ${getATRPercentageLevel()}`}>
                          {indicators.atr_percentage.toFixed(1)}%
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ATR Analysis Summary */}
                  <div className="indicator-summary" style={{
                    borderColor: getATRPercentageColor(),
                    boxShadow: `0 2px 4px ${getATRPercentageColor()}40`
                  }}>
                    {atrAnalysis}
                  </div>
                </div>
              )}

              {/* PIVOT LEVELS Section */}
              {(indicators.pivot_point || indicators.resistance_r1 || indicators.resistance_r2 || indicators.resistance_r3 || indicators.support_s1 || indicators.support_s2 || indicators.support_s3) && (
                <div className="indicator-section">
                  <h3 className="section-title">PIVOT LEVELS (CLASSIC, FIBONACCI, CAMARILLA)</h3>
                  
                  <div className="pivot-levels-container">
                    <div className="pivot-levels-list">
                      {/* Resistance Levels */}
                      {(indicators.resistance_r1 || indicators.resistance_r2 || indicators.resistance_r3) && (
                        <>
                          {indicators.resistance_r3 && (
                            <div className="pivot-level-item">
                              <div style={{ marginBottom: '4px', fontSize: '0.9rem', color: '#9ca3af' }}>Resistance Levels:</div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span className="pivot-level-label">R3:</span>
                                <span className={`pivot-level-value ${getPivotLevelColor('r3').isForecast ? 'animate-pulse' : ''}`} style={{
                                  color: getPivotLevelColor('r3').color,
                                  borderColor: getPivotLevelColor('r3').hasBorder ? getPivotLevelColor('r3').borderColor : 'transparent',
                                  opacity: getPivotLevelColor('r3').opacity,
                                  backgroundColor: getPivotLevelColor('r3').isDimmed ? 'rgba(107, 114, 128, 0.1)' : 'transparent',
                                  fontWeight: getPivotLevelColor('r3').fontWeight || 'normal',
                                  fontSize: getPivotLevelColor('r3').fontSize || 'inherit',
                                  boxShadow: getPivotLevelColor('r3').isForecast ? `0 0 0 2px ${getPivotLevelColor('r3').borderColor}40` : 'none'
                                }}>${formatIndicatorValue(indicators.resistance_r3)}</span>
                              </div>
                            </div>
                          )}
                          {!indicators.resistance_r3 && indicators.resistance_r2 && (
                            <div className="pivot-level-item">
                              <div style={{ marginBottom: '4px', fontSize: '0.9rem', color: '#9ca3af' }}>Resistance Levels:</div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span className="pivot-level-label">R2:</span>
                                <span className={`pivot-level-value ${getPivotLevelColor('r2').isForecast ? 'animate-pulse' : ''}`} style={{
                                  color: getPivotLevelColor('r2').color,
                                  borderColor: getPivotLevelColor('r2').hasBorder ? getPivotLevelColor('r2').borderColor : 'transparent',
                                  opacity: getPivotLevelColor('r2').opacity,
                                  backgroundColor: getPivotLevelColor('r2').isDimmed ? 'rgba(107, 114, 128, 0.1)' : 'transparent',
                                  fontWeight: getPivotLevelColor('r2').fontWeight || 'normal',
                                  fontSize: getPivotLevelColor('r2').fontSize || 'inherit',
                                  boxShadow: getPivotLevelColor('r2').isForecast ? `0 0 0 2px ${getPivotLevelColor('r2').borderColor}40` : 'none'
                                }}>${formatIndicatorValue(indicators.resistance_r2)}</span>
                              </div>
                            </div>
                          )}
                          {!indicators.resistance_r3 && !indicators.resistance_r2 && indicators.resistance_r1 && (
                            <div className="pivot-level-item">
                              <div style={{ marginBottom: '4px', fontSize: '0.9rem', color: '#9ca3af' }}>Resistance Levels:</div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span className="pivot-level-label">R1:</span>
                                <span className={`pivot-level-value ${getPivotLevelColor('r1').isForecast ? 'animate-pulse' : ''}`} style={{
                                  color: getPivotLevelColor('r1').color,
                                  borderColor: getPivotLevelColor('r1').hasBorder ? getPivotLevelColor('r1').borderColor : 'transparent',
                                  opacity: getPivotLevelColor('r1').opacity,
                                  backgroundColor: getPivotLevelColor('r1').isDimmed ? 'rgba(107, 114, 128, 0.1)' : 'transparent',
                                  fontWeight: getPivotLevelColor('r1').fontWeight || 'normal',
                                  fontSize: getPivotLevelColor('r1').fontSize || 'inherit',
                                  boxShadow: getPivotLevelColor('r1').isForecast ? `0 0 0 2px ${getPivotLevelColor('r1').borderColor}40` : 'none'
                                }}>${formatIndicatorValue(indicators.resistance_r1)}</span>
                              </div>
                            </div>
                          )}
                          {indicators.resistance_r2 && indicators.resistance_r3 && (
                            <div className="pivot-level-item">
                              <span className="pivot-level-label">R2:</span>
                              <span className={`pivot-level-value ${getPivotLevelColor('r2').isForecast ? 'animate-pulse' : ''}`} style={{
                                color: getPivotLevelColor('r2').color,
                                borderColor: getPivotLevelColor('r2').hasBorder ? getPivotLevelColor('r2').borderColor : 'transparent',
                                opacity: getPivotLevelColor('r2').opacity,
                                backgroundColor: getPivotLevelColor('r2').isDimmed ? 'rgba(107, 114, 128, 0.1)' : 'transparent',
                                fontWeight: getPivotLevelColor('r2').fontWeight || 'normal',
                                fontSize: getPivotLevelColor('r2').fontSize || 'inherit',
                                boxShadow: getPivotLevelColor('r2').isForecast ? `0 0 0 2px ${getPivotLevelColor('r2').borderColor}40` : 'none'
                              }}>${formatIndicatorValue(indicators.resistance_r2)}</span>
                            </div>
                          )}
                          {indicators.resistance_r1 && (indicators.resistance_r2 || indicators.resistance_r3) && (
                            <div className="pivot-level-item">
                              <span className="pivot-level-label">R1:</span>
                              <span className={`pivot-level-value ${getPivotLevelColor('r1').isForecast ? 'animate-pulse' : ''}`} style={{
                                color: getPivotLevelColor('r1').color,
                                borderColor: getPivotLevelColor('r1').hasBorder ? getPivotLevelColor('r1').borderColor : 'transparent',
                                opacity: getPivotLevelColor('r1').opacity,
                                backgroundColor: getPivotLevelColor('r1').isDimmed ? 'rgba(107, 114, 128, 0.1)' : 'transparent',
                                fontWeight: getPivotLevelColor('r1').fontWeight || 'normal',
                                fontSize: getPivotLevelColor('r1').fontSize || 'inherit',
                                boxShadow: getPivotLevelColor('r1').isForecast ? `0 0 0 2px ${getPivotLevelColor('r1').borderColor}40` : 'none'
                              }}>${formatIndicatorValue(indicators.resistance_r1)}</span>
                            </div>
                          )}
                        </>
                      )}
                      
                      {/* Pivot Point */}
                      {indicators.pivot_point && (
                        <div className="pivot-level-item">
                          <span className="pivot-level-label">P:</span>
                          <span className={`pivot-level-value ${getPivotLevelColor('pivot').isForecast ? 'animate-pulse' : ''}`} style={{
                            color: getPivotLevelColor('pivot').color,
                            borderColor: getPivotLevelColor('pivot').hasBorder ? getPivotLevelColor('pivot').borderColor : 'transparent',
                            opacity: getPivotLevelColor('pivot').opacity,
                            backgroundColor: getPivotLevelColor('pivot').isDimmed ? 'rgba(107, 114, 128, 0.1)' : 'transparent',
                            fontWeight: getPivotLevelColor('pivot').fontWeight || 'normal',
                            fontSize: getPivotLevelColor('pivot').fontSize || 'inherit',
                            boxShadow: getPivotLevelColor('pivot').isForecast ? `0 0 0 2px ${getPivotLevelColor('pivot').borderColor}40` : 'none'
                          }}>${formatIndicatorValue(indicators.pivot_point)}</span>
                        </div>
                      )}

                      {/* Support Levels */}
                      {(indicators.support_s1 || indicators.support_s2 || indicators.support_s3) && (
                        <>
                          {indicators.support_s1 && (
                            <div className="pivot-level-item">
                              <div style={{ marginBottom: '4px', fontSize: '0.9rem', color: '#9ca3af' }}>Support Levels:</div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span className="pivot-level-label">S1:</span>
                                <span className={`pivot-level-value ${getPivotLevelColor('s1').isForecast ? 'animate-pulse' : ''}`} style={{
                                  color: getPivotLevelColor('s1').color,
                                  borderColor: getPivotLevelColor('s1').hasBorder ? getPivotLevelColor('s1').borderColor : 'transparent',
                                  opacity: getPivotLevelColor('s1').opacity,
                                  backgroundColor: getPivotLevelColor('s1').isDimmed ? 'rgba(107, 114, 128, 0.1)' : 'transparent',
                                  fontWeight: getPivotLevelColor('s1').fontWeight || 'normal',
                                  fontSize: getPivotLevelColor('s1').fontSize || 'inherit',
                                  boxShadow: getPivotLevelColor('s1').isForecast ? `0 0 0 2px ${getPivotLevelColor('s1').borderColor}40` : 'none'
                                }}>${formatIndicatorValue(indicators.support_s1)}</span>
                              </div>
                            </div>
                          )}
                          {!indicators.support_s1 && indicators.support_s2 && (
                            <div className="pivot-level-item">
                              <div style={{ marginBottom: '4px', fontSize: '0.9rem', color: '#9ca3af' }}>Support Levels:</div>
                              <span className="pivot-level-label">S2:</span>
                                <span className={`pivot-level-value ${getPivotLevelColor('s2').isForecast ? 'animate-pulse' : ''}`} style={{
                                  color: getPivotLevelColor('s2').color,
                                  borderColor: getPivotLevelColor('s2').hasBorder ? getPivotLevelColor('s2').borderColor : 'transparent',
                                  opacity: getPivotLevelColor('s2').opacity,
                                  backgroundColor: getPivotLevelColor('s2').isDimmed ? 'rgba(107, 114, 128, 0.1)' : 'transparent',
                                  fontWeight: getPivotLevelColor('s2').fontWeight || 'normal',
                                  fontSize: getPivotLevelColor('s2').fontSize || 'inherit',
                                  boxShadow: getPivotLevelColor('s2').isForecast ? `0 0 0 2px ${getPivotLevelColor('s2').borderColor}40` : 'none'
                                }}>${formatIndicatorValue(indicators.support_s2)}</span>
                              </div>
                          )}
                          {!indicators.support_s1 && !indicators.support_s2 && indicators.support_s3 && (
                            <div className="pivot-level-item">
                              <div style={{ marginBottom: '4px', fontSize: '0.9rem', color: '#9ca3af' }}>Support Levels:</div>
                              <span className="pivot-level-label">S3:</span>
                                <span className={`pivot-level-value ${getPivotLevelColor('s3').isForecast ? 'animate-pulse' : ''}`} style={{
                                  color: getPivotLevelColor('s3').color,
                                  borderColor: getPivotLevelColor('s3').hasBorder ? getPivotLevelColor('s3').borderColor : 'transparent',
                                  opacity: getPivotLevelColor('s3').opacity,
                                  backgroundColor: getPivotLevelColor('s3').isDimmed ? 'rgba(107, 114, 128, 0.1)' : 'transparent',
                                  fontWeight: getPivotLevelColor('s3').fontWeight || 'normal',
                                  fontSize: getPivotLevelColor('s3').fontSize || 'inherit',
                                  boxShadow: getPivotLevelColor('s3').isForecast ? `0 0 0 2px ${getPivotLevelColor('s3').borderColor}40` : 'none'
                                }}>${formatIndicatorValue(indicators.support_s3)}</span>
                              </div>
                          )}
                          {indicators.support_s2 && indicators.support_s1 && (
                            <div className="pivot-level-item">
                              <span className="pivot-level-label">S2:</span>
                              <span className={`pivot-level-value ${getPivotLevelColor('s2').isForecast ? 'animate-pulse' : ''}`} style={{
                                color: getPivotLevelColor('s2').color,
                                borderColor: getPivotLevelColor('s2').hasBorder ? getPivotLevelColor('s2').borderColor : 'transparent',
                                opacity: getPivotLevelColor('s2').opacity,
                                backgroundColor: getPivotLevelColor('s2').isDimmed ? 'rgba(107, 114, 128, 0.1)' : 'transparent',
                                fontWeight: getPivotLevelColor('s2').fontWeight || 'normal',
                                fontSize: getPivotLevelColor('s2').fontSize || 'inherit',
                                boxShadow: getPivotLevelColor('s2').isForecast ? `0 0 0 2px ${getPivotLevelColor('s2').borderColor}40` : 'none'
                              }}>${formatIndicatorValue(indicators.support_s2)}</span>
                            </div>
                          )}
                          {indicators.support_s3 && (indicators.support_s1 || indicators.support_s2) && (
                            <div className="pivot-level-item">
                              <span className="pivot-level-label">S3:</span>
                              <span className={`pivot-level-value ${getPivotLevelColor('s3').isForecast ? 'animate-pulse' : ''}`} style={{
                                color: getPivotLevelColor('s3').color,
                                borderColor: getPivotLevelColor('s3').hasBorder ? getPivotLevelColor('s3').borderColor : 'transparent',
                                opacity: getPivotLevelColor('s3').opacity,
                                backgroundColor: getPivotLevelColor('s3').isDimmed ? 'rgba(107, 114, 128, 0.1)' : 'transparent',
                                fontWeight: getPivotLevelColor('s3').fontWeight || 'normal',
                                fontSize: getPivotLevelColor('s3').fontSize || 'inherit',
                                boxShadow: getPivotLevelColor('s3').isForecast ? `0 0 0 2px ${getPivotLevelColor('s3').borderColor}40` : 'none'
                              }}>${formatIndicatorValue(indicators.support_s3)}</span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Summary/Interpretation Box */}
                  <div className="pivot-summary-box" style={{
                    borderColor: getPivotLevelColor('pivot').hasBorder ? getPivotLevelColor('pivot').color : '#6b7280',
                    boxShadow: getPivotLevelColor('pivot').hasBorder ? `0 2px 4px ${getPivotLevelColor('pivot').color}40` : 'none'
                  }}>
                    <div className="pivot-summary-content">
                      <span className="pivot-summary-icon">⚖️</span>
                      <span className="pivot-summary-text">
                        {getPivotAnalysis()}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* News & Sentiment Section */}
          {indicators && Object.keys(indicators).length > 0 && cryptoData && (
            <div className="indicator-section">
              <h3 className="indicator-title">NEWS & SENTIMENT</h3>
              <div className="indicator-card">
                <div className="indicator-content">
                  {isNewsLoading ? (
                    <div className="news-loading">
                      <div className="news-loading-spinner"></div>
                                                <span className="news-loading-text">Loading news and sentiment...</span>
                    </div>
                  ) : newsData && sentimentData ? (
                    <>
                      {/* News Section */}
                      <div className="news-headlines-section">
                        <div className="headlines-list">
                          {newsData && Array.isArray(newsData) && newsData.length > 0 ? (
                            <>
                              {newsData.slice(0, articlesToShow).map((article, index) => {
                                // Calculate time ago from publishedDate
                                let timeAgo = "Recently";
                                try {
                                  const publishedDate = new Date(article.publishedDate);
                                  const now = new Date();
                                  const timeDiff = now - publishedDate;
                                  const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
                                  const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                                  const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
                                  
                                  if (days > 0) {
                                    timeAgo = `${days}d ago`;
                                  } else if (hours > 0) {
                                    timeAgo = `${hours}h ago`;
                                  } else if (minutes > 0) {
                                    timeAgo = `${minutes}m ago`;
                                  }
                                } catch (e) {
                                  timeAgo = "Recently";
                                }
                                
                                // Extract publisher from site
                                let publisher = 'Unknown';
                                if (article.site) {
                                  try {
                                    const cleanSite = article.site.replace('https://', '').replace('http://', '');
                                    const domain = cleanSite.split('.')[0];
                                    publisher = domain.charAt(0).toUpperCase() + domain.slice(1);
                                  } catch (e) {
                                    publisher = 'Unknown';
                                  }
                                }
                                
                                // Create summary from text
                                const summary = article.text ? (article.text.length > 150 ? article.text.substring(0, 150) + '...' : article.text) : '';
                                
                                // Check if it's a trusted publisher
                                const trustedPublishers = ['coindesk', 'theblock', 'bloomberg', 'decrypt', 'cointelegraph', 'forbes', 'reuters', 'cnbc'];
                                const isTrusted = trustedPublishers.some(pub => article.site && article.site.toLowerCase().includes(pub));
                                
                                return (
                                  <div key={index} className="news-card" style={{
                                    marginBottom: '20px',
                                    background: 'linear-gradient(135deg, #1a1d24 0%, #14161A 100%)',
                                    borderRadius: '16px',
                                    border: `2px solid ${isTrusted ? 'rgba(96, 165, 250, 0.3)' : 'rgba(255, 255, 255, 0.08)'}`,
                                    overflow: 'hidden',
                                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 4px 8px rgba(0, 0, 0, 0.2)',
                                    position: 'relative',
                                    transition: 'all 0.3s ease'
                                  }}>
                                    <div className="news-card-content" style={{
                                      padding: '24px'
                                    }}>
                                      {/* Publisher Badge */}
                                      <div className="news-source" style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        marginBottom: '12px'
                                      }}>
                                        <span style={{
                                          fontSize: '0.875rem',
                                          fontWeight: '700',
                                          color: isTrusted ? '#60a5fa' : '#9ca3af',
                                          textTransform: 'uppercase',
                                          letterSpacing: '0.5px'
                                        }}>
                                          {publisher}
                                        </span>
                                        <span style={{
                                          fontSize: '0.75rem',
                                          color: '#6b7280',
                                          marginLeft: 'auto'
                                        }}>
                                          {timeAgo}
                                        </span>
                                      </div>
                                      
                                      {/* Article Title */}
                                      <div className="news-title" style={{
                                        fontSize: '1.125rem',
                                        fontWeight: '700',
                                        color: '#ffffff',
                                        marginBottom: '16px',
                                        lineHeight: '1.4',
                                        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
                                        cursor: 'pointer'
                                      }}
                                      onClick={() => window.open(article.url, '_blank')}
                                      title="Click to read full article">
                                        {article.title}
                                      </div>
                                      
                                      {/* Article Summary */}
                                      <div className="news-summary" style={{
                                        fontSize: '0.9rem',
                                        color: '#d1d5db',
                                        marginBottom: '20px',
                                        lineHeight: '1.6',
                                        opacity: '0.9'
                                      }}>
                                        {summary}
                                      </div>
                                      
                                      {/* Action Bar */}
                                      <div className="news-actions" style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        borderTop: '1px solid rgba(255, 255, 255, 0.05)',
                                        paddingTop: '16px'
                                      }}>
                                        <button 
                                          onClick={() => window.open(article.url, '_blank')}
                                          style={{
                                            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '8px',
                                            padding: '8px 16px',
                                            fontSize: '0.875rem',
                                            fontWeight: '600',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)'
                                          }}
                                          onMouseOver={(e) => {
                                            e.target.style.transform = 'translateY(-1px)';
                                            e.target.style.boxShadow = '0 4px 8px rgba(59, 130, 246, 0.4)';
                                          }}
                                          onMouseOut={(e) => {
                                            e.target.style.transform = 'translateY(0)';
                                            e.target.style.boxShadow = '0 2px 4px rgba(59, 130, 246, 0.3)';
                                          }}
                                        >
                                          Read More
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                              
                              {/* Load More/Less Buttons */}
                              <div style={{
                                textAlign: 'center',
                                marginTop: '20px',
                                marginBottom: '20px'
                              }}>
                                {articlesToShow < newsData.length && (
                                  <button
                                    onClick={() => setArticlesToShow(Math.min(articlesToShow + 5, newsData.length))}
                                    style={{
                                      background: 'linear-gradient(135deg, #374151 0%, #1f2937 100%)',
                                      color: '#d1d5db',
                                      border: '2px solid rgba(255, 255, 255, 0.1)',
                                      borderRadius: '12px',
                                      padding: '12px 24px',
                                      fontSize: '0.9rem',
                                      fontWeight: '600',
                                      cursor: 'pointer',
                                      transition: 'all 0.3s ease',
                                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                                      marginRight: '10px'
                                    }}
                                    onMouseOver={(e) => {
                                      e.target.style.transform = 'translateY(-2px)';
                                      e.target.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.4)';
                                      e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                                    }}
                                    onMouseOut={(e) => {
                                      e.target.style.transform = 'translateY(0)';
                                      e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
                                      e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                                    }}
                                  >
                                    Load More
                                  </button>
                                )}
                                {articlesToShow > 3 && (
                                  <button
                                    onClick={() => setArticlesToShow(3)}
                                    style={{
                                      background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
                                      color: '#ffffff',
                                      border: '2px solid rgba(255, 255, 255, 0.1)',
                                      borderRadius: '12px',
                                      padding: '12px 24px',
                                      fontSize: '0.9rem',
                                      fontWeight: '600',
                                      cursor: 'pointer',
                                      transition: 'all 0.3s ease',
                                      boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)'
                                    }}
                                    onMouseOver={(e) => {
                                      e.target.style.transform = 'translateY(-2px)';
                                      e.target.style.boxShadow = '0 6px 16px rgba(220, 38, 38, 0.4)';
                                      e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                                    }}
                                    onMouseOut={(e) => {
                                      e.target.style.transform = 'translateY(0)';
                                      e.target.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.3)';
                                      e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                                    }}
                                  >
                                    Load Less
                                  </button>
                                )}
                              </div>
                            </>
                                    ) : (
                <div className="news-loading">
                  <div className="news-loading-spinner"></div>
                  <span className="news-loading-text">Loading news articles...</span>
                </div>
            )}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="news-error" style={{
                      textAlign: 'center',
                      padding: '40px 20px',
                      color: '#ef4444'
                    }}>
                      <div style={{
                        marginBottom: '12px',
                        fontSize: '1.5rem',
                        opacity: '0.5'
                      }}>
                        ⚠️
                      </div>
                      <span className="news-error-text" style={{
                        fontSize: '1rem',
                        fontWeight: '500'
                      }}>
                        Unable to load news and sentiment data. Please try again later.
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* AI Summary Section - Only show after main data has loaded */}
          {indicators && Object.keys(indicators).length > 0 && cryptoData && (
            <div className="ai-summary-section">
              <div className="ai-summary-header">
                <span className="ai-summary-icon">🤖</span>
                <h3 className="ai-summary-title">AI Market Analysis</h3>
              </div>
              <div className="ai-summary-content">
                {isAiLoading ? (
                  <div className="ai-loading">
                    <div className="ai-loading-spinner"></div>
                    <span className="ai-loading-text">Generating comprehensive AI analysis...</span>
                  </div>
                ) : aiSummary ? (
                  <div className="ai-summary-text">
                    {aiSummary}
                  </div>
                ) : (
                  <div className="ai-summary-placeholder">
                    <span className="ai-placeholder-text">AI analysis will appear here once data is loaded.</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const StockRow = ({ stock, rank, onClick, onRetryPrice }) => {
  const isPositive = stock.change >= 0;
  
  // Function to get display symbol (base asset without USD suffix)
  const getDisplaySymbol = (symbol) => {
    if (!symbol) return '';
    
    // Special cases for stablecoins that should keep their full names
    if (symbol === 'USDTUSD') return 'USDT';  // Tether
    if (symbol === 'USDCUSD') return 'USDC';  // USD Coin
    if (symbol === 'DAIUSD') return 'DAI';    // Dai
    
    // For other cryptocurrencies, remove USD suffix
    return symbol.replace(/USD$/, '');
  };
  

  

  
  // Function to format market cap with proper T/B/M formatting
  const formatMarketCap = (marketCap) => {
    if (!marketCap || marketCap === 0) return 'N/A';
    
    if (marketCap >= 1000000000000) {
      // Trillions
      return '$' + formatWithCommas(marketCap / 1000000000000, 1) + 'T';
    } else if (marketCap >= 1000000000) {
      // Billions
      return '$' + formatWithCommas(marketCap / 1000000000, 1) + 'B';
    } else if (marketCap >= 1000000) {
      // Millions
      return '$' + formatWithCommas(marketCap / 1000000, 1) + 'M';
    } else {
      return '$' + formatWithCommas(marketCap, 0);
    }
  };
  
  // Using SimpleCryptoLogo component for all crypto logos
  
  return (
    <div className="stock-row" onClick={() => onClick(stock.symbol, stock.name)}>
      <div className="stock-rank">#{rank}</div>
      <div className="stock-info">
        <div className="stock-symbol-icon">
          <div className="stock-logo">
            {/* Using exact same logo logic as holdings page */}
            {console.log('🔍 MAIN PAGE - Symbol:', stock.symbol, 'Name:', stock.name, 'Logo symbol:', stock.symbol)}
            <SimpleCryptoLogo 
              symbol={stock.symbol}
              alt={`${stock.name} logo`}
              width={32}
              height={32}
            />
          </div>
          <div className="stock-details">
            <div className="stock-name">{stock.name}</div>
            <div className="stock-symbol-text">
              {getDisplaySymbol(stock.symbol)}
              {stock.sector && stock.sector !== 'N/A' && (
                <span className="stock-sector"> • {stock.sector}</span>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="stock-price-section">
        {stock.loading ? (
          <div className="stock-price-loading">
            <div className="stock-price-spinner">
              <svg width="16" height="16" viewBox="0 0 24 24" className="spinner-svg">
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                  strokeLinecap="round"
                  strokeDasharray="62.83"
                  strokeDashoffset="62.83"
                  className="spinner-circle"
                />
              </svg>
            </div>
            <div className="stock-loading-text">Loading...</div>
          </div>
        ) : stock.priceError ? (
          <div className="stock-price-error">
            <div className="stock-price">—</div>
            <div className="stock-change positive">0.00%</div>
            <div className="stock-error-message">
              {stock.errorMessage || 'Failed to load'}
            </div>
            <button 
              className="retry-button"
              onClick={(e) => {
                e.stopPropagation();
                onRetryPrice(stock.symbol);
              }}
            >
              Try Again
            </button>
          </div>
        ) : (
          <>
            <div className="stock-price">
              $<FormattedPrice priceData={formatCryptoPrice(stock.price)} />
            </div>
            <div className={`stock-change ${stock.change_percent !== null ? (isPositive ? 'positive' : 'negative') : 'positive'}`}>
              {formatPercentageChange(stock.change_percent)}
            </div>
          </>
        )}
      </div>
      <div className="stock-market-cap">
        {formatMarketCap(stock.market_cap)}
      </div>
      <button className="analyze-button" onClick={(e) => {
        e.stopPropagation();
        onClick(stock.symbol, stock.name);
      }}>
        Analyze
      </button>
    </div>
  );
};

const StockDetail = ({ symbol, onBack }) => {
  const [stockData, setStockData] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzingLoading, setAnalyzingLoading] = useState(false);

  useEffect(() => {
    fetchStockDetails();
  }, [symbol]);

  const fetchStockDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/stocks/${symbol}`);
      setStockData(response.data);
    } catch (error) {
      console.error('Error fetching stock details:', error);
    } finally {
      setLoading(false);
    }
  };

  const analyzeStock = async () => {
    try {
      setAnalyzingLoading(true);
      const response = await axios.post(`${API}/stocks/${symbol}/analyze`);
      setAnalysis(response.data);
    } catch (error) {
      console.error('Error analyzing stock:', error);
    } finally {
      setAnalyzingLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading cryptocurrency data...</p>
      </div>
    );
  }

  if (!stockData) {
    return (
      <div className="error-container">
        <p>Error loading cryptocurrency data</p>
        <button onClick={onBack} className="back-button">Back to Cryptocurrencies</button>
      </div>
    );
  }

  const { stock, profile } = stockData;
  const isPositive = stock.change >= 0;

  return (
    <div className="stock-detail">
      <div className="stock-detail-header">
        <button onClick={onBack} className="back-button">← Back to Stocks</button>
        <h1>{stock.name} ({stock.symbol})</h1>
      </div>

      <div className="stock-main-info">
        <div className="price-section">
          <div className="current-price">$<FormattedPrice priceData={formatCryptoPrice(stock.price)} /></div>
          <div className={`price-change ${isPositive ? 'positive' : 'negative'}`}>
            {isPositive ? '+' : ''}$<FormattedPrice priceData={formatCryptoPrice(stock.change)} /> ({formatPercentageChange(stock.change_percent)})
          </div>
        </div>

        <div className="stock-stats-grid">
          <div className="stat-item">
            <span className="stat-label">Market Cap</span>
            <span className="stat-value">
                              {stock.market_cap ? `$${formatWithCommas(stock.market_cap / 1000000000, 1)}B` : 'N/A'}
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">High</span>
            <span className="stat-value">$<FormattedPrice priceData={formatCryptoPrice(stock.high)} /></span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Low</span>
            <span className="stat-value">$<FormattedPrice priceData={formatCryptoPrice(stock.low)} /></span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Volume</span>
            <span className="stat-value">{stock.volume ? stock.volume.toLocaleString() : 'N/A'}</span>
          </div>
        </div>
      </div>

      <div className="analysis-section">
        <div className="analysis-header">
          <h2>AI Analysis</h2>
          <button 
            onClick={analyzeStock} 
            disabled={analyzingLoading}
            className="analyze-button"
          >
            {analyzingLoading ? 'Analyzing...' : 'Generate Analysis'}
          </button>
        </div>
        
        {analysis && (
          <div className="analysis-result">
            <p>{analysis.analysis}</p>
          </div>
        )}
      </div>
    </div>
  );
};

const Home = () => {
  // Legacy state for compatibility (still needed by existing functions)
  const [activeRequests, setActiveRequests] = useState(new Map()); // Track active AbortControllers
  const [priceData, setPriceData] = useState(new Map()); // Map from symbol to price data
  const [loadingStockPrices, setLoadingStockPrices] = useState(new Set()); // Track loading prices
  const [allCompanies, setAllCompanies] = useState([]); // Legacy companies list
  const [priceCache, setPriceCache] = useState(new Map()); // Price cache
  const [loadingPrices, setLoadingPrices] = useState(new Set()); // Loading prices set
  const [pageDataCache] = useState(() => new Map()); // Page data cache
  const [activePageRefreshers] = useState(() => new Set()); // Active refreshers

  // Existing state
  const [stocks, setStocks] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalStocks, setTotalStocks] = useState(500);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [serverTotalPages, setServerTotalPages] = useState(5);
  const [isNavigating, setIsNavigating] = useState(false);
  const [navigationError, setNavigationError] = useState(null);
  
  // Pagination settings for new 500-stock system
  const STOCKS_PER_PAGE = 100; // Changed from 20 to 100
  const TOTAL_STOCKS = 500;     // Changed from 100 to 500  
  const TOTAL_PAGES = 5;        // Same as before (500 ÷ 100 = 5)
  
  // Cache and performance state
  const [cacheStatus, setCacheStatus] = useState({});
  const [requestCount, setRequestCount] = useState(0); // Track concurrent request count
  const [pageNavigationTimeout, setPageNavigationTimeout] = useState(null); // Track navigation timeout
  const [lastUserInteraction, setLastUserInteraction] = useState(Date.now()); // Track user activity
  const [backgroundRefreshInterval, setBackgroundRefreshInterval] = useState(null); // Track background refresh
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  const [nextUpdateCountdown, setNextUpdateCountdown] = useState(10);
  
  // Modal and UI state
  const [selectedStock, setSelectedStock] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalSymbol, setModalSymbol] = useState('');
  const [modalStockName, setModalStockName] = useState('');

  // Search state
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredCryptos, setFilteredCryptos] = useState([]);
  const [lastLiveFetch, setLastLiveFetch] = useState(null);
  
  // Unified crypto cache for client-side search
  const [allCryptosCache, setAllCryptosCache] = useState([]);
  const [cryptoCacheLoaded, setCryptoCacheLoaded] = useState(false);
  const [isClientSearch, setIsClientSearch] = useState(false);
  const [displayedCryptos, setDisplayedCryptos] = useState([]);

  const currentPageRef = useRef(1);
  const [autoRefreshInterval, setAutoRefreshInterval] = useState(null);
  const loadingRequestRef = useRef(null);

  // Navigate to specific page using new page-based API
  const navigateToPage = async (pageNumber) => {
    console.log(`🎯 NAVIGATE: Moving from page ${currentPage} to page ${pageNumber} (stocks ${(pageNumber-1)*100 + 1}-${pageNumber*100})`);
    
    try {
      // CRITICAL: Clear previous page data first to prevent mixing
      setStocks([]);
      setLoading(true);
      
      // Track leaving the current page (for user counting)
      if (currentPage && currentPage !== pageNumber) {
        await axios.post(`${API}/page/${currentPage}/leave`).catch(e => 
          console.warn(`Failed to track leaving page ${currentPage}:`, e)
        );
      }
      
      // Call page visit tracking for new page
      await axios.post(`${API}/page/${pageNumber}/visit`);
      
      // Fetch page data (will be auto-cached on backend if needed)
        const response = await axios.get(`${API}/page/${pageNumber}`);
      const pageData = response.data;
      
      if (pageData && pageData.stocks) {
        // CRITICAL: Ensure we only use page-specific data
        const pageStocks = Array.isArray(pageData.stocks) ? pageData.stocks : [];
        
        // Convert object to array if needed (backend sometimes returns object)
        let stocksArray;
        if (Array.isArray(pageStocks)) {
          stocksArray = pageStocks;
        } else {
          stocksArray = Object.values(pageStocks);
        }
        
        // Sort by rank to ensure proper order
        stocksArray.sort((a, b) => (a.rank || 0) - (b.rank || 0));
        
        setStocks(stocksArray);
        setCurrentPage(pageNumber);
        currentPageRef.current = pageNumber; // Update ref for auto-refresh
        setLoading(false);
        
        console.log(`✅ NAVIGATE: Page ${pageNumber} loaded with ${stocksArray.length} stocks`);
        console.log(`📊 FIRST_STOCK: ${stocksArray[0]?.symbol} (rank ${stocksArray[0]?.rank})`);
        console.log(`📊 LAST_STOCK: ${stocksArray[stocksArray.length-1]?.symbol} (rank ${stocksArray[stocksArray.length-1]?.rank})`);
        console.log(`⏱ UPDATE_STRATEGY: ${pageData.update_strategy}`);
        console.log(`👥 ACTIVE_USERS: ${pageData.active_users} users viewing this page`);
        
        // For Pages 2-5: Ensure auto-refresh is active for the new page
        if (pageNumber > 1) {
          console.log(`🔄 AUTO_REFRESH: Ensuring auto-refresh is active for Page ${pageNumber}`);
          // Auto-refresh is already running and will now refresh this page since currentPageRef is updated
        }
      } else {
        console.error('❌ NAVIGATE: Invalid page data received');
        setLoading(false);
      }
    } catch (error) {
      console.error('❌ NAVIGATE: Error loading page data:', error);
      setLoading(false);
    }
  };

  // Fetch all 500 cryptos data from unified cache for client-side search
  const fetchAllCryptosData = async () => {
    try {
      console.log('🚀 Fetching all 500 cryptos data for client-side search...');
      const response = await axios.get(`${API}/cryptos/all`);
      const { cryptos, total } = response.data;
      
      setAllCryptosCache(cryptos);
      setCryptoCacheLoaded(true);
      
      console.log(`✅ Cached ${total} cryptocurrencies for client-side search`);
      console.log(`📊 Cache update time: ${response.data.last_updated}`);
      
      return cryptos;
    } catch (error) {
      console.error('❌ Error fetching all cryptos data:', error);
      setCryptoCacheLoaded(false);
      return [];
    }
  };
  
  // Debounced search function for client-side search (300ms delay)
  const debouncedClientSearch = useMemo(
    () => {
      const debouncedFn = debounce((query) => {
        console.log(`📞 DEBOUNCED_CALL: Executing search for "${query}"`);
        performClientSideSearch(query);
      }, 300);
      
      return debouncedFn;
    },
    [allCryptosCache] // Only recreate if the cache changes
  );
  
  // Client-side search function with atomic state updates
  const performClientSideSearch = useCallback((query) => {
    console.log(`🔍 SEARCH_CALL: performClientSideSearch called with "${query}"`);
    console.log(`🔄 CURRENT_STATE: isClientSearch=${isClientSearch}, displayedCryptos.length=${displayedCryptos.length}`);
    
    if (!query || query.trim().length === 0) {
      // Clear search - return to pagination mode
      console.log('🔄 CLEARING: Detected empty query, clearing search...');
      
      // Atomic state update for clearing
      setIsClientSearch(false);
      setDisplayedCryptos([]);
      setCurrentPage(1); 
      
      console.log('🔄 Search cleared - returning to page 1 of pagination mode');
      return;
    }
    
    if (!cryptoCacheLoaded || allCryptosCache.length === 0) {
      console.log('⚠️ Crypto cache not loaded yet, cannot search');
      return;
    }
    
    console.log(`🔍 SEARCH_START: Searching for "${query}" in ${allCryptosCache.length} cryptocurrencies`);
    console.log(`📊 CACHE_STATE: allCryptosCache has ${allCryptosCache.length} items`);
    
    const queryLower = query.toLowerCase();
    const matchedCryptos = allCryptosCache.filter(crypto => {
      const symbolMatch = crypto.symbol.toLowerCase().includes(queryLower);
      const nameMatch = crypto.name.toLowerCase().includes(queryLower);
      return symbolMatch || nameMatch;
    });
    
    console.log(`🎯 SEARCH_FILTER: Found ${matchedCryptos.length} raw matches for "${query}"`);
    
    // Sort by relevance: exact symbol matches first, then name matches, then partial matches
    const sortedMatches = matchedCryptos.sort((a, b) => {
      const aSymbol = a.symbol.toLowerCase();
      const bSymbol = b.symbol.toLowerCase();
      const aName = a.name.toLowerCase();
      const bName = b.name.toLowerCase();
      
      // Exact symbol match gets highest priority
      if (aSymbol === queryLower && bSymbol !== queryLower) return -1;
      if (bSymbol === queryLower && aSymbol !== queryLower) return 1;
      
      // Symbol starts with query gets next priority
      if (aSymbol.startsWith(queryLower) && !bSymbol.startsWith(queryLower)) return -1;
      if (bSymbol.startsWith(queryLower) && !aSymbol.startsWith(queryLower)) return 1;
      
      // Name starts with query gets next priority
      if (aName.startsWith(queryLower) && !bName.startsWith(queryLower)) return -1;
      if (bName.startsWith(queryLower) && !aName.startsWith(queryLower)) return 1;
      
      // Finally sort by market cap (rank)
      return a.rank - b.rank;
    });
    
    // Remove duplicates by symbol to prevent accumulation
    const uniqueMatches = sortedMatches.filter((crypto, index, self) =>
      index === self.findIndex(c => c.symbol === crypto.symbol)
    );
    
    console.log(`🧹 DEDUP_FILTER: After deduplication: ${uniqueMatches.length} unique matches`);
    console.log(`🔄 SEARCH_UPDATE: Setting search state atomically`);
    console.log(`📋 MATCH_SYMBOLS: ${uniqueMatches.slice(0, 3).map(c => c.symbol).join(', ')}`);
    
    // Atomic state updates using functional updates to ensure fresh state
    setDisplayedCryptos(() => uniqueMatches); // Force replacement, not append
    setIsClientSearch(() => true);
    setCurrentPage(() => 1);
    
    console.log(`✅ SEARCH_COMPLETE: Set ${uniqueMatches.length} matches for "${query}"`);
    
    if (uniqueMatches.length > 0) {
      console.log(`🎯 Top matches: ${uniqueMatches.slice(0, 3).map(c => `${c.symbol} (${c.name})`).join(', ')}`);
    }
  }, [cryptoCacheLoaded, allCryptosCache, isClientSearch, displayedCryptos.length]);

  // Handle search input changes with debouncing
  const handleSearchInput = useCallback((e) => {
    const query = e.target.value;
    console.log(`⌨️ INPUT_CHANGE: User typed "${query}" (previous: "${searchQuery}")`);
    
    // Cancel any pending searches to prevent accumulation
    if (debouncedClientSearch && debouncedClientSearch.cancel) {
      debouncedClientSearch.cancel();
    }
    
    setSearchQuery(query);
    setSearchTerm(query); // Keep for compatibility

    console.log(`🔍 SEARCH INPUT: "${query}"`);
    
    // Use debounced client search
    debouncedClientSearch(query);
  }, [debouncedClientSearch, searchQuery]);

  // Clear search and reset with atomic state updates
  const clearSearch = useCallback(() => {
    console.log('🧹 CLEAR_SEARCH: Starting atomic search clear process...');
    console.log(`🔄 PRE_CLEAR_STATE: isClientSearch=${isClientSearch}, displayedCryptos.length=${displayedCryptos.length}, currentPage=${currentPage}`);
    
    // Cancel any pending debounced searches first
    if (debouncedClientSearch && debouncedClientSearch.cancel) {
      debouncedClientSearch.cancel();
      console.log('🚫 DEBOUNCE_CANCEL: Cancelled pending search calls');
    }
    
    // Atomic state clearing - use functional updates to ensure fresh state
    setSearchQuery(() => '');
    setSearchTerm(() => '');
    setIsClientSearch(() => false);
    setDisplayedCryptos(() => []);
    setCurrentPage(() => 1);
    
    // Force navigation back to page 1 to ensure proper data
    setTimeout(() => {
      navigateToPage(1);
      console.log('🔄 RESET: Navigation to page 1 completed');
    }, 100); // Small delay to ensure state updates are applied
    
    console.log('🔄 CLEAR_COMPLETE: Cleared search - returning to page 1 of pagination mode');
  }, [navigateToPage, debouncedClientSearch, isClientSearch, displayedCryptos.length, currentPage]);

  useEffect(() => {
    loadInitialData();
    
    // Track initial visit to Page 1
    axios.post(`${API}/page/1/visit`).catch(e => 
      console.warn('Failed to track initial Page 1 visit:', e)
    );
    
    // Start auto-refresh after initial data is loaded
    const timer = setTimeout(() => {
      startAutoRefresh();
    }, 2000); // Wait 2 seconds after initial load
    
    // Setup background price maintenance
    setupBackgroundRefresh();
    
    // Setup user interaction tracking
    setupUserInteractionTracking();
    
    // Setup crypto cache refresh (every 30 seconds)
    const cryptoCacheRefreshInterval = setInterval(() => {
      if (cryptoCacheLoaded) {
        console.log('🔄 Refreshing crypto cache for search...');
        fetchAllCryptosData();
      }
    }, 30000); // Refresh every 30 seconds
    
    // Cleanup function to cancel all requests when component unmounts
    return () => {
      clearTimeout(timer);
      clearInterval(cryptoCacheRefreshInterval);
      
      // Cancel any pending debounced searches
      if (debouncedClientSearch && debouncedClientSearch.cancel) {
        debouncedClientSearch.cancel();
        console.log('🧹 CLEANUP: Cancelled pending search calls');
      }
      
      stopAutoRefresh();
      activeRequests.forEach(controller => {
        if (controller && !controller.signal.aborted) {
          controller.abort();
        }
      });
      
      if (backgroundRefreshInterval) {
        clearInterval(backgroundRefreshInterval);
      }
      
      // Track leaving current page when component unmounts
      const pageToLeave = currentPageRef.current;
      if (pageToLeave) {
        axios.post(`${API}/page/${pageToLeave}/leave`).catch(e => 
          console.warn(`Failed to track leaving page ${pageToLeave} on unmount:`, e)
        );
      }
    };
  }, []);

  // Setup user interaction tracking to prevent unnecessary cleanups
  const setupUserInteractionTracking = () => {
    const updateLastInteraction = () => {
      setLastUserInteraction(Date.now());
    };

    // Track various user interactions
    document.addEventListener('click', updateLastInteraction);
    document.addEventListener('keydown', updateLastInteraction);
    document.addEventListener('scroll', updateLastInteraction);
    document.addEventListener('mousemove', updateLastInteraction);

    return () => {
      document.removeEventListener('click', updateLastInteraction);
      document.removeEventListener('keydown', updateLastInteraction);
      document.removeEventListener('scroll', updateLastInteraction);
      document.removeEventListener('mousemove', updateLastInteraction);
    };
  };

  // Setup background refresh to maintain prices during long sessions
  const setupBackgroundRefresh = () => {
    const intervalId = setInterval(() => {
      const timeSinceInteraction = Date.now() - lastUserInteraction;
      
      // Only refresh if user has been active in the last 10 minutes
      if (timeSinceInteraction < 10 * 60 * 1000) {
        console.log('Background refresh: maintaining stock prices');
        maintainStockPrices();
      }
    }, 2 * 60 * 1000); // Every 2 minutes

    setBackgroundRefreshInterval(intervalId);
  };

  // Maintain stock prices in background without disrupting UI
  const maintainStockPrices = () => {
    const currentStocks = stocks.filter(stock => stock.symbol);
    const stalePrices = currentStocks.filter(stock => {
      const cached = priceData.get(stock.symbol);
      if (!cached) return true;
      
      // Consider prices stale after 5 minutes
      const cacheAge = Date.now() - (cached.timestamp || 0);
      return cacheAge > 5 * 60 * 1000;
    });

    if (stalePrices.length > 0) {
      console.log(`Refreshing ${stalePrices.length} stale prices in background`);
      
      stalePrices.forEach((stock, index) => {
        setTimeout(() => {
          if (!loadingStockPrices.has(stock.symbol)) {
            // Refresh entire page from centralized cache instead of individual stocks
            navigateToPage(currentPage);
          }
        }, index * 200); // Stagger background requests
      });
    }
  };

  // Enhanced initialization with CONTROLLED loading to prevent multiple calls
  useEffect(() => {
    console.log('🔄 INIT: useEffect called - Component initialized/refreshed - performing state reset');
    
    // Clear any stale request tracking state that might persist after refresh
    const resetState = () => {
      setActiveRequests(new Map());
      setLoadingStockPrices(new Set());
      setRequestCount(0);
      setIsNavigating(false);
      setNavigationError(null);
      setPageNavigationTimeout(null);
    };
    
    resetState();
    
    // Initialize last interaction time
    setLastUserInteraction(Date.now());
    
    console.log('✅ INIT: Request management state reset');
    
    // CRITICAL: Only load data if not already loaded to prevent multiple calls
    if (allCompanies.length === 0) {
      console.log('🚀 INIT: Starting initial data load...');
      loadInitialData();
    } else {
      console.log('⏭️ INIT: Data already loaded, skipping initial load');
    }
    
  }, []); // Empty dependency array - run only once on mount

  // COMPREHENSIVE NAVIGATION AND LOADING STATE MANAGEMENT  
  const [navigationId, setNavigationId] = useState(0); // Unique ID for each navigation to prevent race conditions
  const [loadingTimeout, setLoadingTimeout] = useState(null);
  const [pageLoadStart, setPageLoadStart] = useState(null);

  // BULLETPROOF Debounced navigation with comprehensive error handling
  const debouncedNavigate = useCallback(
    debounce((pageNumber) => {
      if (isNavigating) {
        console.log(`🚫 NAV_BUSY: Already navigating, skipping page ${pageNumber}`);
        return;
      }
      
      const previousPage = currentPage;
      
      if (pageNumber !== previousPage) {
        if (allCompanies.length === 0) {
          console.log('⏳ NO_DATA: Waiting for initial data...');
          setNavigationError('Loading initial data, please wait...');
          return;
        }
        
        const navId = Date.now();
        setNavigationId(navId);
        console.log(`🎯 NAV_DEBOUNCED: Starting navigation ${previousPage} → ${pageNumber} (ID: ${navId})`);
        
        setIsNavigating(true);
        setNavigationError(null);
        setLoading(true);
        
        // Clear any existing timeouts
        if (pageNavigationTimeout) {
          clearTimeout(pageNavigationTimeout);
          setPageNavigationTimeout(null);
        }
        
        // Navigation timeout - If page load takes too long, show error with retry
        const timeoutId = setTimeout(() => {
          if (isNavigating && navigationId === navId) {
            console.log(`⏰ NAV_TIMEOUT: Page ${pageNumber} navigation timeout`);
            setNavigationError('Navigation took longer than expected. Please try again.');
            setIsNavigating(false);
            setLoading(false);
          }
        }, 8000); // 8 second timeout
        
        setPageNavigationTimeout(timeoutId);
        
        // Perform navigation
        performPageNavigation(pageNumber, navId)
          .then(() => {
            if (navigationId === navId) {
              console.log(`✅ NAV_COMPLETE: Successfully navigated to page ${pageNumber}`);
              setIsNavigating(false);
              setLoading(false);
              
              // Clear timeout on success
              if (pageNavigationTimeout) {
                clearTimeout(pageNavigationTimeout);
                setPageNavigationTimeout(null);
              }
            }
          })
          .catch((error) => {
            if (navigationId === navId) {
              console.error(`❌ NAV_FAILED: Navigation to page ${pageNumber} failed:`, error);
              setNavigationError(`Failed to load page ${pageNumber}. Please try again.`);
              setIsNavigating(false);
              setLoading(false);
              
              // Clear timeout on error
              if (pageNavigationTimeout) {
                clearTimeout(pageNavigationTimeout);
                setPageNavigationTimeout(null);
              }
            }
          });
      }
    }, 400),
    [isNavigating, currentPage, allCompanies, pageNavigationTimeout, navigationId]
  );

  // CRITICAL: Comprehensive navigation function with bulletproof loading management
  const performPageNavigation = async (pageNumber, navId) => {
    try {
      console.log(`🎯 NAV_START: Performing navigation to page ${pageNumber} (navId: ${navId})`);
      
      // Check if navigation was cancelled
      if (navigationId !== navId) {
        console.log(`🚫 NAV_CANCELLED: Navigation ${navId} was superseded by ${navigationId}`);
        return;
      }
      
      // Load page data from centralized cache
      await navigateToPage(pageNumber);
      
      // Check again if navigation was cancelled
      if (navigationId !== navId) {
        console.log(`🚫 NAV_CANCELLED: Navigation ${navId} cancelled during load`);
        return;
      }
      
      console.log(`✅ NAV_SUCCESS: Successfully navigated to page ${pageNumber}`);
      
    } catch (error) {
      console.error(`❌ NAV_ERROR: Navigation to page ${pageNumber} failed:`, error);
      throw error;
    }
  };

  // Cancel requests for specific symbols (used during page navigation)
  const cancelRequestsForPage = (currentPageSymbols) => {
    console.log(`Cancelling requests not needed for current page...`);
    
    // Only cancel requests that are NOT for the current page
    activeRequests.forEach((controller, symbol) => {
      if (!currentPageSymbols.includes(symbol) && controller && !controller.signal.aborted) {
        controller.abort();
        console.log(`Cancelled request for ${symbol} (not needed for current page)`);
      }
    });
  };

  // Retry navigation function
  const retryNavigation = () => {
    setNavigationError(null);
    if (currentPage === 1) {
      // If on page 1, retry initial load
      loadInitialData();
    } else {
      // Otherwise, retry page navigation
      performPageNavigation(currentPage);
    }
  };

  // Refresh page function
  const refreshPage = () => {
    window.location.reload();
  };



  // Load initial data from Page 1 (homepage)
  const loadInitialData = async () => {
    try {
      console.log('🚀 LOADING: loadInitialData function called!');
      console.log('🔗 API URL:', `${API}/page/1`);
      console.log('🌐 BACKEND_URL:', BACKEND_URL);
      setLoading(true);
      console.log('🚀 LOADING: Starting initial data load - Page 1...');
      
      // Test backend connectivity first
      try {
        console.log('🔍 Testing backend connectivity...');
        const testResponse = await axios.get(`${BACKEND_URL}/api/`, { timeout: 5000 });
        console.log('✅ Backend connectivity test successful:', testResponse.status);
      } catch (testError) {
        console.error('❌ Backend connectivity test failed:', testError.message);
        console.error('❌ Test error details:', testError);
      }
      
      // Parallel loading: Page 1 data + All cryptos cache
      console.log('📡 Making API calls...');
      const [pageResponse, allCryptosResponse] = await Promise.allSettled([
        axios.get(`${API}/page/1`, { timeout: 10000 }),
        fetchAllCryptosData()
      ]);
      
      console.log('📊 Page response status:', pageResponse.status);
      console.log('📊 All cryptos response status:', allCryptosResponse.status);
      
      // Handle Page 1 data
      if (pageResponse.status === 'fulfilled') {
        const pageData = pageResponse.value.data;
        console.log('🏠 API: Page 1 data loaded successfully');
        console.log('📦 Page data structure:', Object.keys(pageData));
        console.log('📦 Stocks array length:', pageData.stocks?.length);
        
        let stocksArray = [];
        
        if (pageData && pageData.stocks) {
          // Handle both array and object formats from backend
          if (Array.isArray(pageData.stocks)) {
            stocksArray = pageData.stocks;
            console.log('✅ Stocks is an array with', stocksArray.length, 'items');
          } else {
            stocksArray = Object.values(pageData.stocks);
            console.log('✅ Stocks is an object, converted to array with', stocksArray.length, 'items');
          }
          
          // Sort by rank to ensure proper sequential order
          stocksArray.sort((a, b) => (a.rank || 0) - (b.rank || 0));
          
          // Ensure loading property is set correctly based on whether we have price data
          stocksArray = stocksArray.map(stock => ({
            ...stock,
            loading: !stock.price && stock.price !== 0, // Only show loading if price is null/undefined, not if it's 0
            priceError: false,
            errorMessage: null
          }));
          
          console.log('📊 First stock:', stocksArray[0]);
          console.log('📊 Last stock:', stocksArray[stocksArray.length - 1]);
          
          setStocks(stocksArray);
          setCurrentPage(1);
          currentPageRef.current = 1;
          setTotalStocks(pageData.total_stocks || 500);
          setServerTotalPages(TOTAL_PAGES);
          
          console.log(`✅ LOADED: Page 1 with ${stocksArray.length} stocks`);
          console.log(`📊 FIRST_STOCK: ${stocksArray[0]?.symbol} (rank ${stocksArray[0]?.rank})`);
          console.log(`📊 LAST_STOCK: ${stocksArray[stocksArray.length-1]?.symbol} (rank ${stocksArray[stocksArray.length-1]?.rank})`);
        } else {
          console.error('❌ No stocks data in page response');
          console.error('❌ Page data:', pageData);
        }
        
        // Legacy companies data for fallback compatibility
        if (stocksArray.length > 0) {
          const companiesFromStocks = stocksArray.map(stock => ({
            symbol: stock.symbol,
            name: stock.name,
            rank: stock.rank,
            page: 1
          }));
          setAllCompanies(companiesFromStocks);
          console.log(`🔍 LEGACY_COMPANIES: Set ${companiesFromStocks.length} companies for legacy search fallback`);
        }
      } else {
        console.error('❌ Page 1 data loading failed');
        console.error('❌ Page response error:', pageResponse.reason);
        setNavigationError('Failed to load cryptocurrency data. Please refresh the page.');
      }
      
      // Handle All Cryptos Cache data
      if (allCryptosResponse.status === 'fulfilled') {
        console.log('💾 CRYPTO_CACHE: All cryptos data loaded for search');
      } else {
        console.warn('⚠️ Crypto cache loading failed, search may be limited');
        console.warn('⚠️ All cryptos response error:', allCryptosResponse.reason);
      }
      
      console.log(`🎯 READY: Initial data load completed - ready for navigation and search`);
      
    } catch (error) {
      console.error('❌ ERROR: Failed to load initial data:', error);
      console.error('❌ Error details:', error.response?.data || error.message);
      console.error('❌ Error status:', error.response?.status);
      setNavigationError('Failed to load cryptocurrency data. Please refresh the page.');
    } finally {
      setLoading(false);
      console.log('🏁 LOADING: Initial data load completed');
    }
  };

  // BULLETPROOF Load stocks for a specific page with race condition protection
  const loadStocksForPageFromCache = async (page, companies = allCompanies, navId = null) => {
    try {
      console.log(`📄 LOAD_PAGE: Loading page ${page} (nav ID: ${navId})`);
      
      if (companies.length === 0) {
        console.log('❌ NO_COMPANIES: Company data not available');
        setLoading(false);
        setNavigationError('Company data not loaded. Please refresh the page.');
        setIsNavigating(false);
        return;
      }
      
      // Calculate pagination for current page
      const startIndex = (page - 1) * STOCKS_PER_PAGE;
      const endIndex = startIndex + STOCKS_PER_PAGE;
      const pageCompanies = companies.slice(startIndex, endIndex);
      
      console.log(`📊 PAGE_DATA: Loading ${pageCompanies.length} companies for page ${page}`);
      
      // Create initial stock objects with cached data
      const initialStocks = pageCompanies.map((company, index) => ({
        symbol: company.symbol,
        name: company.name,
        sector: company.sector || 'N/A',
        rank: startIndex + index + 1,
        // Check for cached price data
        price: priceData.has(company.symbol) ? priceData.get(company.symbol).price : null,
        change: priceData.has(company.symbol) ? priceData.get(company.symbol).change : null,
        change_percent: priceData.has(company.symbol) ? priceData.get(company.symbol).change_percent : null,
        market_cap: priceData.has(company.symbol) ? priceData.get(company.symbol).market_cap : null,
        loading: !priceData.has(company.symbol), // Show loading only if no cached data
        hasPrice: priceData.has(company.symbol) ? priceData.get(company.symbol).has_price : false,
        hasChange: priceData.has(company.symbol) ? priceData.get(company.symbol).has_change : false,
        hasChangePercent: priceData.has(company.symbol) ? priceData.get(company.symbol).has_change_percent : false,

        priceError: false,
        errorMessage: null,
        loadStartTime: Date.now()
      }));
      
      // CRITICAL: Always update state to show the page (this fixes the blue loading screen issue)
      console.log(`✅ DISPLAY_PAGE: Showing ${initialStocks.length} stocks for page ${page}`);
      setStocks(initialStocks);
      setLoading(false);
      setIsNavigating(false);
      setNavigationError(null);
      
      // Only proceed with price loading if still current navigation (or initial load)
      if (navId === 'initial' || !navId || navigationId === navId) {
        // CONTROLLED PRICE LOADING: Start loading missing prices
        setTimeout(() => {
          if (navId === 'initial' || !navId || navigationId === navId) {
            console.log(`💰 CACHE_APPROACH: Using centralized cache for page ${page} - no individual price loading needed`);
            // With centralized cache, prices are already loaded with the page data
          } else {
            console.log(`⏭️ SKIP_PRICES: Navigation changed, skipping price loading for page ${page}`);
          }
        }, 200); // Short delay for UI responsiveness
      } else {
        console.log(`⏭️ SKIP_UPDATE: Navigation changed from ${navId} to ${navigationId}, skipping page ${page} update`);
      }
      
    } catch (error) {
      console.error(`❌ PAGE_ERROR: Error loading page ${page}:`, error);
      // Always show error to prevent stuck loading screen
      setLoading(false);
      setNavigationError(`Failed to load page ${page}. Please try again.`);
      setIsNavigating(false);
    }
  };

  // Check for stocks that are stuck in loading state and show error
  const checkForStuckLoadingStocks = () => {
    console.log('Checking for stocks stuck in loading state...');
    
    setStocks(prevStocks => 
      prevStocks.map(stock => {
        // Only mark as stuck if loading for more than 15 seconds and not actively being requested
        if (stock.loading && !loadingStockPrices.has(stock.symbol)) {
          const timeSinceLoad = Date.now() - (stock.loadStartTime || Date.now());
          if (timeSinceLoad > 15000) { // 15 seconds
            console.log(`Found stuck loading stock: ${stock.symbol}`);
            
            // Also remove it from loading tracking
            setLoadingStockPrices(prev => {
              const newSet = new Set(prev);
              newSet.delete(stock.symbol);
              return newSet;
            });
            
            return {
              ...stock,
              loading: false,
              priceError: true,
              hasPrice: false,
              hasChange: false,
              hasChangePercent: false,
              errorMessage: 'Loading timeout'
            };
          }
        }
        return stock;
      })
    );
  };

  // Retry loading price for a specific stock
  const retryStockPrice = (symbol) => {
    console.log(`Retrying price load for ${symbol}`);
    
    // Remove from cache to force reload
    setPriceData(prev => {
      const newMap = new Map(prev);
      newMap.delete(symbol);
      return newMap;
    });
    
    // Reset stock state
    setStocks(prevStocks => 
      prevStocks.map(stock => 
        stock.symbol === symbol 
          ? { 
              ...stock, 
              loading: true, 
              priceError: false,
              errorMessage: null,
              loadStartTime: Date.now()
            }
          : stock
      )
    );
    
    // Refresh entire page from centralized cache instead of individual stock
    navigateToPage(currentPage);
  };

  // Refresh all prices for current page
  const refreshAllPrices = () => {
    console.log('Refreshing all prices for current page...');
    
    // Get current page stocks
    const currentStocks = stocks.map(stock => stock.symbol);
    
    // Clear cached data for current stocks
    setPriceData(prev => {
      const newMap = new Map(prev);
      currentStocks.forEach(symbol => newMap.delete(symbol));
      return newMap;
    });
    
    // Reset all stocks to loading state
    setStocks(prevStocks => 
      prevStocks.map(stock => ({
        ...stock,
        loading: true,
        priceError: false,
        errorMessage: null,
        price: null,
        change: null,
        change_percent: null
      }))
    );
    
    // Refresh entire page from centralized cache instead of individual stocks
    navigateToPage(currentPage);
  };

  // Load companies first, then prices progressively
  const loadCompaniesAndPrices = async (page = 1) => {
    // This function is now replaced by loadInitialData and navigateToPage
    await navigateToPage(page);
  };

  // SIMPLIFIED: With centralized cache, individual price loading is no longer needed
  const loadMissingPricesForPage = async (pageCompanies, navId = null) => {
    console.log(`💰 CACHE_APPROACH: Using centralized cache - individual price loading not needed (nav ID: ${navId})`);
    // With centralized cache, all prices are loaded together from the backend cache
    // This function is kept for compatibility but does nothing
    return;
  };


  const fetchTopStocks = async (page = 1) => {
    // Use the new progressive loading approach
    await navigateToPage(page);
  };

  // === REMOVED OLD SEARCH SYSTEM TO PREVENT CONFLICTS === 
  // The old search system has been completely removed to prevent interference 
  // with the new real-time client-side search system

  // Fetch individual stock price
  const fetchStockPrice = async (symbol) => {
    if (priceCache.has(symbol) || loadingPrices.has(symbol)) {
      return; // Already cached or loading
    }

    setLoadingPrices(prev => new Set(prev).add(symbol));

    try {
      const response = await axios.get(`${API}/stocks/price/${symbol}`);
      setPriceCache(prev => new Map(prev).set(symbol, response.data));
    } catch (error) {
      console.error(`Error fetching price for ${symbol}:`, error);
    } finally {
      setLoadingPrices(prev => {
        const newSet = new Set(prev);
        newSet.delete(symbol);
        return newSet;
      });
    }
  };

  // Enhanced search function with shared caching strategy and improved reliability
  // Auto-refresh system - Updates current page every 10 seconds during market hours
  const startAutoRefresh = useCallback(() => {
    console.log('🔄 AUTO_REFRESH: Starting page-specific auto-refresh system');
    
    // Clear any existing interval
    if (autoRefreshInterval) {
      clearInterval(autoRefreshInterval);
    }
    
    // Set up interval to refresh current page every 10 seconds
    const intervalId = setInterval(async () => {
      try {
        const currentPageNumber = currentPageRef.current;
        console.log(`🔄 AUTO_REFRESH: Refreshing page ${currentPageNumber}...`);
        
        const response = await axios.get(`${API}/page/${currentPageNumber}`);
        const pageData = response.data;
        
        if (pageData && pageData.stocks) {
          console.log(`✅ AUTO_REFRESH: Updated page ${currentPageNumber} with ${pageData.stocks.length} stocks`);
          
          // Update timestamp for display
          setLastUpdateTime(new Date().toLocaleTimeString());
          setNextUpdateCountdown(10);
          
          // Update stocks with fresh data
          setStocks(pageData.stocks);
          
          if (currentPageNumber === 1) {
            console.log(`🏠 PAGE_1: Homepage auto-refreshed from backend cache (always maintained)`);
          } else {
            console.log(`📄 PAGE_${currentPageNumber}: Auto-refreshed while user is viewing (backend only fetches if users present)`);
          }
        }
        
      } catch (error) {
        console.error('❌ AUTO_REFRESH: Failed to refresh page data:', error);
      }
    }, 10000); // 10 seconds
    
    setAutoRefreshInterval(intervalId);
    
    // Countdown timer for UI display
    const countdownId = setInterval(() => {
      setNextUpdateCountdown(prev => {
        if (prev <= 1) {
          return 10; // Reset to 10 seconds
        }
        return prev - 1;
      });
    }, 1000);
    
    return { intervalId, countdownId };
  }, [autoRefreshInterval, searchTerm, searchResults.length]);

  const stopAutoRefresh = useCallback(() => {
    console.log('🛑 AUTO_REFRESH: Stopping auto-refresh');
    if (autoRefreshInterval) {
      clearInterval(autoRefreshInterval);
      setAutoRefreshInterval(null);
    }
  }, [autoRefreshInterval]);

  // Start auto-refresh for a specific page
  const startPageAutoRefresh = useCallback((pageNumber) => {
    if (activePageRefreshers.has(pageNumber)) {
      return; // Already refreshing this page
    }

    const intervalId = setInterval(async () => {
      try {
        const pageKey = `page_${pageNumber}`;
        const cached = pageDataCache.get(pageKey);
        
        // Only refresh if cache is older than 10 seconds
        if (!cached || (Date.now() - cached.timestamp) > 10000) {
          console.log(`🔄 PAGE_AUTO_REFRESH: Refreshing page ${pageNumber} data`);
          
          const response = await axios.get(`${API}/cryptos?page=${pageNumber}&limit=100`);
          if (response.data && response.data.stocks) {
            pageDataCache.set(pageKey, {
              data: response.data.stocks,
              timestamp: Date.now()
            });
            console.log(`✅ PAGE_AUTO_REFRESH: Updated page ${pageNumber} cache`);
          }
        }
      } catch (error) {
        console.error(`❌ PAGE_AUTO_REFRESH: Failed to refresh page ${pageNumber}:`, error);
      }
    }, 10000); // 10 seconds

    activePageRefreshers.add(pageNumber);
    
    // Store interval ID for cleanup
    setTimeout(() => {
      clearInterval(intervalId);
      activePageRefreshers.delete(pageNumber);
      console.log(`🛑 PAGE_AUTO_REFRESH: Stopped auto-refresh for page ${pageNumber}`);
    }, 300000); // Stop after 5 minutes
  }, [pageDataCache, activePageRefreshers]);

  // Get current stocks for display (either paginated or search results)
  const getDisplayStocks = () => {
    console.log('🔍 DEBUG: getDisplayStocks called');
    console.log('🔍 DEBUG: stocks.length =', stocks.length);
    console.log('🔍 DEBUG: isClientSearch =', isClientSearch);
    console.log('🔍 DEBUG: searchQuery =', searchQuery);
    
    // If in client search mode, return search results
    if (isClientSearch && searchQuery.trim()) {
      const searchResultsPerPage = 100; // Show 100 results per page in search mode
      const searchTotalPages = Math.ceil(displayedCryptos.length / searchResultsPerPage);
      const searchStartIndex = (currentPage - 1) * searchResultsPerPage;
      const searchEndIndex = searchStartIndex + searchResultsPerPage;
      const paginatedSearchResults = displayedCryptos.slice(searchStartIndex, searchEndIndex);
      
      return {
        stocks: paginatedSearchResults,
        total: displayedCryptos.length,
        totalPages: Math.max(1, searchTotalPages),
        isSearchResult: true,
        searchQuery: searchQuery,
        totalMatches: displayedCryptos.length
      };
    }
    
    // Use regular pagination
    console.log('🔍 DEBUG: Using regular pagination, returning stocks.length =', stocks.length);
    return {
      stocks: stocks,
      total: totalStocks,
      totalPages: serverTotalPages,
      isSearchResult: false
    };
  };

  const displayData = getDisplayStocks();
  const currentStocks = displayData.stocks;
  const totalPages = displayData.totalPages;

  // Smart pagination display logic
  const getPaginationNumbers = () => {
    const pages = [];
    
    if (totalPages <= 7) {
      // Show all pages if 7 or fewer
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        // Show first 5 pages, ellipsis, then last page
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        // Show first page, ellipsis, then last 5 pages
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Show first page, ellipsis, current page area, ellipsis, last page
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const paginationNumbers = getPaginationNumbers();

  const handleStockClick = (symbol, name) => {
    setModalSymbol(symbol);
    setModalStockName(name);
    setModalOpen(true);
  };

  const handleBackToStocks = () => {
    setSelectedStock(null);
  };

  const handlePageChange = (pageNumber) => {
    if (isClientSearch && searchQuery.trim()) {
      // In search mode, just change the page for search results display
      console.log(`🎯 SEARCH_PAGE: Changing to search results page ${pageNumber}`);
      setCurrentPage(pageNumber);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Normal pagination mode
      console.log(`🎯 PAGE_CHANGE: Navigating to page ${pageNumber} using cached data`);
      navigateToPage(pageNumber);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      const newPage = currentPage - 1;
      if (isClientSearch && searchQuery.trim()) {
        console.log(`🎯 SEARCH_PREV: Going to search results page ${newPage}`);
        setCurrentPage(newPage);
      } else {
        console.log(`🎯 PREV_PAGE: Going to page ${newPage}`);
        navigateToPage(newPage);
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      const newPage = currentPage + 1;
      if (isClientSearch && searchQuery.trim()) {
        console.log(`🎯 SEARCH_NEXT: Going to search results page ${newPage}`);
        setCurrentPage(newPage);
      } else {
        console.log(`🎯 NEXT_PAGE: Going to page ${newPage}`);
        navigateToPage(newPage);
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalSymbol('');
    setModalStockName('');
  };

  if (selectedStock) {
    return <StockDetail symbol={selectedStock} onBack={handleBackToStocks} />;
  }

  return (
    <div className="home">
      <header className="hero-section">
        <div className="hero-content">
          <h1 className="hero-title">CryptoIndicators.ai</h1>
          <p className="hero-subtitle">Live cryptocurrency prices with AI-powered technical analysis</p>
          <p className="hero-cross-promotion">
            Want real-time stock analysis too? Explore <a href="https://stockindicators.ai" target="_blank" rel="noopener noreferrer" className="hero-link">StockIndicators.ai</a>
          </p>
        </div>
      </header>

      <main className="main-content">
        <div className="stocks-section">
          {navigationError && (
            <div className="navigation-error">
              <p>{navigationError}</p>
              <div className="error-buttons">
                <button onClick={retryNavigation}>Try Again</button>
                <button onClick={refreshPage}>Refresh Page</button>
              </div>
            </div>
          )}
          
          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>{isNavigating ? 'Navigating...' : 'Loading cryptocurrencies...'}</p>
            </div>
          ) : (
            <>
              <div className="stocks-header">
                <div className="stocks-title-section">
                  {displayData.isSearchResult ? (
                    <div>
                      <h2 className="stocks-title">
                        Search Results for "{displayData.searchQuery}"
                      </h2>
                      <p className="search-results-info">
                        Found {displayData.totalMatches} cryptocurrencies
                        {displayData.totalPages > 1 && ` (Page ${currentPage} of ${displayData.totalPages})`}
                      </p>
                    </div>
                  ) : (
                    <h2 className="stocks-title">Top 500 Cryptocurrencies by Market Cap</h2>
                  )}
                </div>
                
                {/* Real-time search input */}
                <div className="search-section">
                  <div className="search-input-container">
                    <input
                      type="text"
                      className="search-input"
                      placeholder="Search Cryptocurrencies"
                      value={searchQuery}
                      onChange={handleSearchInput}
                    />
                    {searchQuery && (
                      <button 
                        className="search-clear-button"
                        onClick={clearSearch}
                        title="Clear search"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="stocks-list">
                {currentStocks.map((stock, index) => {
                  // Use the rank from backend data instead of calculating locally
                  return (
                    <StockRow 
                      key={stock.symbol} 
                      stock={stock} 
                      rank={stock.rank || index + 1} // Use backend rank, fallback to local index
                      onClick={handleStockClick}
                      onRetryPrice={retryStockPrice}
                    />
                  );
                })}
              </div>
              
              {/* Bottom Pagination Controls */}
              {totalPages > 1 && (
                <div className="pagination-container">
                  <div className="pagination">
                    <button 
                      className="pagination-btn prev" 
                      onClick={handlePrevPage}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                    
                    <div className="page-numbers">
                      {paginationNumbers.map((pageNum, index) => (
                        pageNum === '...' ? (
                          <span key={`ellipsis-${index}`} className="page-ellipsis">...</span>
                        ) : (
                          <button
                            key={pageNum}
                            className={`page-btn ${currentPage === pageNum ? 'active' : ''}`}
                            onClick={() => handlePageChange(pageNum)}
                          >
                            {pageNum}
                          </button>
                        )
                      ))}
                    </div>
                    
                    <button 
                      className="pagination-btn next" 
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <footer className="footer">
        <div className="footer-cross-promotion">
          <p className="cross-promotion-text">
            💡 Looking for AI-powered stock insights too? 
            Check out our sister platform: <a href="https://stockindicators.ai" target="_blank" rel="noopener noreferrer" className="footer-link">StockIndicators.ai</a> for real-time analysis on your favorite stocks.
          </p>
        </div>
        <div className="footer-legal">
          <p>&copy; 2024 CryptoIndicators.ai. All rights reserved.</p>
        </div>
      </footer>

      <IndicatorModal 
        key={modalSymbol}
        isOpen={modalOpen}
        onClose={closeModal}
        symbol={modalSymbol}
        cryptoName={modalStockName}
      />
    </div>
  );
};

const UserDashboard = () => {
  const { currentUser, userProfile } = useAuth();
  const [topMoverColor, setTopMoverColor] = useState('white');
  
  // Smart color mapping based on cryptocurrency brand colors
  const getHoldingColor = (symbol) => {
    const brandColors = {
      // Bitcoin - Orange/Bronze
      'BTC': '#F7931A',
      
      // Ethereum - Blue/Indigo
      'ETH': '#627EEA',
      
      // Solana - Purple/Blue gradient (using primary purple)
      'SOL': '#9945FF',
      
      // Cardano - Blue/White (using primary blue)
      'ADA': '#0033AD',
      
      // Polkadot - Pink/Magenta
      'DOT': '#E6007A',
      
      // Chainlink - Blue
      'LINK': '#2A5ADA',
      
      // Uniswap - Pink/Red
      'UNI': '#FF007A',
      
      // Avalanche - Red/Orange
      'AVAX': '#E84142',
      
      // Polygon - Purple
      'MATIC': '#8247E5',
      
      // Litecoin - Silver/Gray
      'LTC': '#B8B8B8',
      
      // Ripple - Blue
      'XRP': '#23292F',
      
      // Dogecoin - Yellow/Gold
      'DOGE': '#C2A633',
      
      // Shiba Inu - Orange
      'SHIB': '#FF6B35',
      
      // Binance Coin - Yellow
      'BNB': '#F3BA2F',
      
      // Tether - Green
      'USDT': '#26A17B',
      
      // USD Coin - Blue
      'USDC': '#2775CA',
      
      // Dai - Orange
      'DAI': '#F5AC37',
      
      // Chainlink - Blue
      'LTC': '#2A5ADA',
      
      // Default fallback colors for unknown cryptos
      'DEFAULT': [
        '#3B82F6', // Blue
        '#10B981', // Green
        '#F59E0B', // Yellow
        '#EF4444', // Red
        '#8B5CF6', // Purple
        '#06B6D4', // Cyan
        '#F97316', // Orange
        '#EC4899', // Pink
        '#84CC16', // Lime
        '#6366F1', // Indigo
        '#059669', // Emerald
        '#DC2626', // Red
        '#7C3AED', // Violet
        '#EA580C', // Orange
        '#BE185D'  // Rose
      ]
    };
    
    // Return brand color if available, otherwise use default color array
    if (brandColors[symbol]) {
      return brandColors[symbol];
    }
    
    // For unknown symbols, use a hash-based color from default array
    const hash = symbol.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const defaultColors = brandColors.DEFAULT;
    return defaultColors[hash % defaultColors.length];
  };

  // Calculate holding percentage based on USD value including cash
  const calculateHoldingPercentage = (holding, allHoldings) => {
    if (!allHoldings || allHoldings.length === 0) return 0;
    
    // Get current price from the main crypto data
    const currentPrice = getCurrentCryptoPrice(holding.symbol);
    if (!currentPrice) return 0;
    
    const holdingValue = parseFloat(holding.quantity) * currentPrice;
    const cashToInvest = parseFloat(userProfile?.cashToInvest || 0);
    const totalValue = allHoldings.reduce((sum, h) => {
      const price = getCurrentCryptoPrice(h.symbol);
      return sum + (parseFloat(h.quantity) * (price || 0));
    }, 0) + cashToInvest;
    
    if (totalValue === 0) return 0;
    const percentage = (holdingValue / totalValue) * 100;
    return percentage.toFixed(1);
  };

  // Get current crypto price from the main crypto data
  const getCurrentCryptoPrice = (symbol) => {
    // This will be populated when we integrate with the main crypto data
    // For now, return placeholder values
    const placeholderPrices = {
      'BTC': 50000, // $50,000 per BTC
      'ETH': 3200,  // $3,200 per ETH
      'SOL': 150,   // $150 per SOL
      'ADA': 0.5,   // $0.50 per ADA
      'DOT': 7,     // $7 per DOT
      'LINK': 15,   // $15 per LINK
      'UNI': 8,     // $8 per UNI
      'AVAX': 35    // $35 per AVAX
    };
    return placeholderPrices[symbol] || 0;
  };

  // Get 24h change percentage from FMP API for a specific crypto
  const getCrypto24hChange = (symbol) => {
    // TODO: This should fetch from the same FMP API data that feeds the main crypto pages
    // For now, we'll use placeholder data that matches what we see in the main crypto list
    const placeholderChanges = {
      'BTC': 2.45, 'ETH': -1.23, 'SOL': 8.67, 'ADA': -0.89, 'DOT': 3.21,
      'LINK': -2.15, 'UNI': 1.78, 'AVAX': 5.43
    };
    return placeholderChanges[symbol] || 0;
  };

  // Calculate total portfolio value including crypto and cash
  const calculateTotalPortfolioValue = () => {
    const cashToInvest = parseFloat(userProfile?.cashToInvest || 0);
    const cryptoValue = userProfile?.holdings?.reduce((sum, holding) => {
      const price = getCurrentCryptoPrice(holding.symbol);
      return sum + (parseFloat(holding.quantity) * price);
    }, 0) || 0;
    
    const totalValue = cashToInvest + cryptoValue;
    return totalValue.toLocaleString();
  };

  // Calculate cash percentage of total portfolio
  const calculateCashPercentage = () => {
    const cashToInvest = parseFloat(userProfile?.cashToInvest || 0);
    const cryptoValue = userProfile?.holdings?.reduce((sum, holding) => {
      const price = getCurrentCryptoPrice(holding.symbol);
      return sum + (parseFloat(holding.quantity) * price);
    }, 0) || 0;
    
    const totalValue = cashToInvest + cryptoValue;
    if (totalValue === 0) return 0;
    
    const percentage = (cashToInvest / totalValue) * 100;
    return percentage.toFixed(1);
  };

  // Calculate 24h portfolio change percentage
  const calculate24hChange = () => {
    if (!userProfile?.holdings || userProfile.holdings.length === 0) return 0;
    
    // Simulate 24h change based on current holdings
    // In real implementation, this would come from API data
    const totalChange = userProfile.holdings.reduce((sum, holding) => {
      const currentPrice = getCurrentCryptoPrice(holding.symbol);
      const quantity = parseFloat(holding.quantity);
      
      // Simulate 24h change (random between -5% to +15% for demo)
      // Replace with actual API data when available
      const changePercent = (Math.random() * 20 - 5);
      const holdingValue = currentPrice * quantity;
      const changeValue = (holdingValue * changePercent) / 100;
      
      return sum + changeValue;
    }, 0);
    
    const totalValue = parseFloat(calculateTotalPortfolioValue().replace(/,/g, ''));
    if (totalValue === 0) return 0;
    
    const changePercent = (totalChange / totalValue) * 100;
    return changePercent.toFixed(2);
  };

  // Get color for 24h change (green for positive, red for negative)
  const get24hChangeColor = () => {
    const change = parseFloat(calculate24hChange());
    return change >= 0 ? '#10B981' : '#EF4444';
  };

  // Get top 3 holdings by USD value
  const getTopHoldings = () => {
    if (!userProfile?.holdings || userProfile.holdings.length === 0) return [];
    
    // Calculate USD value for each holding and sort by value
    const holdingsWithValue = userProfile.holdings.map(holding => {
      const currentPrice = getCurrentCryptoPrice(holding.symbol);
      const quantity = parseFloat(holding.quantity);
      const usdValue = currentPrice * quantity;
      
      return {
        ...holding,
        usdValue: usdValue,
        currentPrice: currentPrice
      };
    });
    
    // Sort by USD value descending and take top 3
    return holdingsWithValue
      .sort((a, b) => b.usdValue - a.usdValue)
      .slice(0, 3);
  };

  // Get today's top mover (biggest percentage change)
  const getTopMover = () => {
    if (!userProfile?.holdings || userProfile.holdings.length === 0) return null;
    
    // Calculate 24h change for each holding using actual FMP API data
    const holdingsWithChange = userProfile.holdings.map(holding => {
      const currentPrice = getCurrentCryptoPrice(holding.symbol);
      const quantity = parseFloat(holding.quantity);
      const usdValue = currentPrice * quantity;
      
      // Get actual 24h change from FMP API data
      const changePercent = getCrypto24hChange(holding.symbol);
      
      return {
        symbol: holding.symbol,
        name: holding.name,
        quantity: holding.quantity,
        usdValue: usdValue,
        currentPrice: currentPrice,
        changePercent: changePercent
      };
    });
    
    // Find the holding with the biggest absolute change
    const topMover = holdingsWithChange.reduce((max, holding) => {
      return Math.abs(holding.changePercent) > Math.abs(max.changePercent) ? holding : max;
    });
    
    // Format the change percent to 2 decimal places
    topMover.changePercent = parseFloat(topMover.changePercent.toFixed(2));
    
    return topMover;
  };

  // Render the performance line chart
  useEffect(() => {
    const canvas = document.getElementById('portfolioPerformanceChart');
    if (!canvas || !userProfile?.holdings) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    
    // Clear canvas
    ctx.clearRect(0, 0, width, height);
    
    // Generate sample performance data (7 days)
    const days = 7;
    const data = [];
    let currentValue = 50000; // Start with $50k
    
    for (let i = 0; i < days; i++) {
      // Simulate daily changes (random walk)
      const change = (Math.random() - 0.5) * 0.1; // ±5% daily change
      currentValue *= (1 + change);
      data.push(currentValue);
    }
    
    // Find min and max for scaling
    const minValue = Math.min(...data);
    const maxValue = Math.max(...data);
    const range = maxValue - minValue;
    
    // Chart dimensions
    const padding = 20;
    const chartWidth = width - (padding * 2);
    const chartHeight = height - (padding * 2);
    
    // Draw grid lines
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
    ctx.lineWidth = 1;
    
    // Horizontal grid lines
    for (let i = 0; i <= 4; i++) {
      const y = padding + (i * chartHeight / 4);
      ctx.beginPath();
      ctx.moveTo(padding, y);
      ctx.lineTo(width - padding, y);
      ctx.stroke();
    }
    
    // Vertical grid lines
    for (let i = 0; i <= 6; i++) {
      const x = padding + (i * chartWidth / 6);
      ctx.beginPath();
      ctx.moveTo(x, padding);
      ctx.lineTo(x, height - padding);
      ctx.stroke();
    }
    
    // Draw performance line
    ctx.strokeStyle = '#3B82F6';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    ctx.beginPath();
    data.forEach((value, index) => {
      const x = padding + (index * chartWidth / (days - 1));
      const y = padding + chartHeight - ((value - minValue) / range * chartHeight);
      
      if (index === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });
    ctx.stroke();
    
    // Draw data points
    ctx.fillStyle = '#3B82F6';
    data.forEach((value, index) => {
      const x = padding + (index * chartWidth / (days - 1));
      const y = padding + chartHeight - ((value - minValue) / range * chartHeight);
      
      ctx.beginPath();
      ctx.arc(x, y, 4, 0, 2 * Math.PI);
      ctx.fill();
    });
    
    // Add gradient fill below the line
    const gradient = ctx.createLinearGradient(0, padding, 0, height - padding);
    gradient.addColorStop(0, 'rgba(59, 130, 246, 0.2)');
    gradient.addColorStop(1, 'rgba(59, 130, 246, 0.05)');
    
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.moveTo(padding, height - padding);
    data.forEach((value, index) => {
      const x = padding + (index * chartWidth / (days - 1));
      const y = padding + chartHeight - ((value - minValue) / range * chartHeight);
      ctx.lineTo(x, y);
    });
    ctx.lineTo(width - padding, height - padding);
    ctx.closePath();
    ctx.fill();
    
  }, [userProfile?.holdings]);

  // Handle top mover color animation
  useEffect(() => {
    if (getTopMover()) {
      const changePercent = getTopMover().changePercent;
      
      // Start with white
      setTopMoverColor('white');
      
      // After 1 second, fade to color (green for positive, red for negative)
      const colorTimer = setTimeout(() => {
        const targetColor = changePercent >= 0 ? '#10B981' : '#EF4444';
        setTopMoverColor(targetColor);
        
        // After 3 seconds, fade back to white
        const whiteTimer = setTimeout(() => {
          setTopMoverColor('white');
        }, 3000);
        
        return () => clearTimeout(whiteTimer);
      }, 1000);
      
      return () => clearTimeout(colorTimer);
    }
  }, [userProfile?.holdings]);

  // Render the top mover mini chart
  useEffect(() => {
    const canvas = document.getElementById('topMoverChart');
    if (!canvas || !getTopMover()) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    
    // Clear canvas
    ctx.clearRect(0, 0, width, height);
    
    // Generate mini performance data (24 hours)
    const hours = 24;
    const data = [];
    let currentValue = 100; // Start with 100 (baseline)
    
    for (let i = 0; i < hours; i++) {
      // Simulate hourly changes with more volatility
      const change = (Math.random() - 0.5) * 0.15; // ±7.5% hourly change
      currentValue *= (1 + change);
      data.push(currentValue);
    }
    
    // Find min and max for scaling
    const minValue = Math.min(...data);
    const maxValue = Math.max(...data);
    const range = maxValue - minValue;
    
    // Chart dimensions
    const padding = 10;
    const chartWidth = width - (padding * 2);
    const chartHeight = height - (padding * 2);
    
    // Draw mini performance line
    ctx.strokeStyle = getTopMover()?.changePercent >= 0 ? '#10B981' : '#EF4444';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    ctx.beginPath();
    data.forEach((value, index) => {
      const x = padding + (index * chartWidth / (hours - 1));
      const y = padding + chartHeight - ((value - minValue) / range * chartHeight);
      
      if (index === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });
    ctx.stroke();
    
    // Draw data points (smaller)
    ctx.fillStyle = getTopMover()?.changePercent >= 0 ? '#10B981' : '#EF4444';
    data.forEach((value, index) => {
      if (index % 4 === 0) { // Show every 4th point to avoid clutter
        const x = padding + (index * chartWidth / (hours - 1));
        const y = padding + chartHeight - ((value - minValue) / range * chartHeight);
        
        ctx.beginPath();
        ctx.arc(x, y, 2, 0, 2 * Math.PI);
        ctx.fill();
      }
    });
    
  }, [userProfile?.holdings]);

  // Render the pie chart with animation
  useEffect(() => {
    const canvas = document.getElementById('portfolioPieChart');
    if (!canvas || !userProfile?.holdings) return;

    const ctx = canvas.getContext('2d');
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = Math.min(centerX, centerY) - 10;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Calculate total value including cash to invest
    const cashToInvest = parseFloat(userProfile.cashToInvest || 0);
    const holdingsValue = userProfile.holdings.reduce((sum, holding) => {
      const price = getCurrentCryptoPrice(holding.symbol);
      return sum + (parseFloat(holding.quantity) * (price || 0));
    }, 0);
    const totalValue = holdingsValue + cashToInvest;

    if (totalValue === 0) return;

    // Prepare data including cash to invest
    const portfolioData = [
      ...userProfile.holdings.map(holding => ({
        type: 'crypto',
        symbol: holding.symbol,
        name: holding.name,
        quantity: holding.quantity,
        value: parseFloat(holding.quantity) * getCurrentCryptoPrice(holding.symbol)
      })),
      {
        type: 'cash',
        symbol: 'CASH',
        name: 'Cash to Invest',
        quantity: cashToInvest,
        value: cashToInvest
      }
    ];

    // Sort by value (largest first for better visual)
    const sortedData = portfolioData.sort((a, b) => b.value - a.value);

    // Animation variables
    let currentAngle = 0;
    let targetAngle = 0;
    let animationProgress = 0;
    const animationDuration = 1000; // 1 second
    const startTime = Date.now();

    // Animation function
    const animate = () => {
      const elapsed = Date.now() - startTime;
      animationProgress = Math.min(elapsed / animationDuration, 1);
      
      // Easing function for smooth animation
      const easeOut = 1 - Math.pow(1 - animationProgress, 3);
      
      // Calculate current angle with spinning effect
      currentAngle = easeOut * targetAngle + (1 - easeOut) * 720; // Start with 2 full rotations
      
      // Clear and redraw
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Draw pie chart
      let startAngle = currentAngle;
      sortedData.forEach((item, index) => {
        const sliceAngle = (item.value / totalValue) * 2 * Math.PI;
        
        // Draw slice
        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.arc(centerX, centerY, radius, startAngle, startAngle + sliceAngle);
        ctx.closePath();
        ctx.fillStyle = item.type === 'cash' ? '#10B981' : getHoldingColor(item.symbol);
        ctx.fill();
        
        // Add border
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
        ctx.lineWidth = 2;
        ctx.stroke();
        
        startAngle += sliceAngle;
      });
      
      // Continue animation if not complete
      if (animationProgress < 1) {
        requestAnimationFrame(animate);
      }
    };

    // Start animation
    animate();
  }, [userProfile?.holdings]);

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1 className="dashboard-title">My Dashboard</h1>
        <p className="dashboard-subtitle">Your personalized crypto trading overview</p>
      </div>
      
      <div className="dashboard-content">
        {/* Portfolio Value Summary */}
        <div className="portfolio-value-summary">
          <div className="portfolio-value">
            Portfolio Value: ${calculateTotalPortfolioValue()}
          </div>
        </div>

        <div className="dashboard-grid">
          {/* Portfolio Distribution */}
          <div className="dashboard-card">
            <h3 className="card-title">Portfolio Distribution</h3>
            <div className="portfolio-chart-container">
              <div className="pie-chart-wrapper">
                <canvas 
                  id="portfolioPieChart" 
                  width="200" 
                  height="200"
                  className="portfolio-pie-chart"
                ></canvas>
                <div className="pie-chart-center">
                  <div className="total-value">
                    ${calculateTotalPortfolioValue()}
                  </div>
                  <div className="total-label">Total Value</div>
                </div>
              </div>
              <div className="portfolio-legend">
                {userProfile?.holdings && userProfile.holdings.length > 0 ? (
                  <>
                    {/* Cash to Invest */}
                    {parseFloat(userProfile.cashToInvest || 0) > 0 && (
                      <div className="legend-item">
                        <div className="legend-color" style={{ backgroundColor: '#10B981' }}></div>
                        <div className="legend-content">
                          <div className="legend-logo">
                            <span style={{ fontSize: '20px', color: '#10B981' }}>💰</span>
                          </div>
                          <div className="legend-text">
                            <div className="legend-symbol">CASH</div>
                            <div className="legend-name">Cash to Invest</div>
                          </div>
                          <div className="legend-percentage">
                            {calculateCashPercentage()}%
                          </div>
                        </div>
                      </div>
                    )}
                    {/* Crypto Holdings */}
                    {userProfile.holdings.map((holding, index) => (
                      <div key={index} className="legend-item">
                        <div className="legend-color" style={{ backgroundColor: getHoldingColor(holding.symbol) }}></div>
                        <div className="legend-content">
                          <div className="legend-logo">
                            <SimpleCryptoLogo 
                              symbol={holding.symbol + 'USD'} 
                              width={20} 
                              height={20} 
                            />
                          </div>
                          <div className="legend-text">
                            <div className="legend-symbol">{holding.symbol}</div>
                            <div className="legend-name">{holding.name}</div>
                          </div>
                          <div className="legend-percentage">
                            {calculateHoldingPercentage(holding, userProfile.holdings)}%
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                ) : (
                  <div className="no-holdings-legend">No holdings to display</div>
                )}
              </div>
            </div>
          </div>

          {/* Portfolio Performance Chart */}
          <div className="dashboard-card performance-card">
            <h3 className="card-title">Portfolio Performance</h3>
            <div className="performance-chart-container">
              <canvas 
                id="portfolioPerformanceChart" 
                width="300" 
                height="150"
                className="performance-line-chart"
              ></canvas>
              <div className="performance-metrics">
                <div className="metric-item">
                  <span className="metric-label">24h Change:</span>
                  <span className="metric-value" style={{ color: get24hChangeColor() }}>
                    {calculate24hChange()}%
                  </span>
                </div>
                <div className="metric-item">
                  <span className="metric-label">Total Value:</span>
                  <span className="metric-value">
                    ${calculateTotalPortfolioValue()}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Today's Top Mover (embedded below performance) */}
            <div className="top-mover-embedded">
              <h4 className="mover-subtitle">Today's Top Mover</h4>
              {getTopMover() ? (
                <>
                  <div className="mover-header-compact">
                    <div className="mover-logo-compact">
                      <SimpleCryptoLogo 
                        symbol={getTopMover().symbol + 'USD'} 
                        width={24} 
                        height={24} 
                      />
                    </div>
                    <div className="mover-info-compact">
                      <div className="mover-symbol-compact">{getTopMover().symbol}</div>
                      <div className="mover-name-compact">{getTopMover().name}</div>
                    </div>
                  </div>
                  <div className="mover-performance-compact">
                    <div className="mover-change-compact" style={{ color: topMoverColor, transition: 'color 0.5s ease' }}>
                      {getTopMover().changePercent >= 0 ? '+' : '-'}{Math.abs(getTopMover().changePercent)}%
                    </div>
                    <div className="mover-value-compact">
                      ${getTopMover().usdValue.toLocaleString()}
                    </div>
                  </div>
                </>
              ) : (
                <div className="no-mover-message-compact">
                  <span className="no-mover-icon-compact">📊</span>
                  <p>No holdings to track</p>
                </div>
              )}
            </div>
          </div>



          {/* Top Holdings */}
          <div className="dashboard-card">
            <h3 className="card-title">Top Holdings</h3>
            <div className="top-holdings-container">
              {getTopHoldings().length > 0 ? (
                getTopHoldings().map((holding, index) => (
                  <div key={index} className="top-holding-item">
                    <div className="holding-info">
                      <div className="holding-logo">
                        <SimpleCryptoLogo 
                          symbol={holding.symbol + 'USD'} 
                          width={24} 
                          height={24} 
                        />
                      </div>
                      <div className="holding-details">
                        <div className="holding-symbol">{holding.symbol}</div>
                        <div className="holding-name">{holding.name}</div>
                      </div>
                    </div>
                    <div className="holding-values">
                      <div className="holding-amount">{holding.quantity}</div>
                      <div className="holding-usd-value">${holding.usdValue.toLocaleString()}</div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-holdings-message">
                  <span className="no-holdings-icon">💰</span>
                  <p>No holdings yet</p>
                  <p className="no-holdings-subtitle">Add crypto to your portfolio to see your top holdings here</p>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="dashboard-card">
            <h3 className="card-title">Quick Actions</h3>
            <div className="quick-actions">
              <button className="action-button primary">
                View Crypto Market
              </button>
              <button className="action-button secondary">
                Update Profile
              </button>
              <button className="action-button secondary">
                View Analytics
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProtectedDashboard = () => {
  const { currentUser, userProfile, signOut } = useAuth();
  const [activeView, setActiveView] = useState('crypto'); // 'crypto' or 'dashboard'

  // Check if user wants to see dashboard after onboarding
  useEffect(() => {
    const showDashboard = localStorage.getItem('showDashboard');
    if (showDashboard === 'true') {
      setActiveView('dashboard');
      // Clear the flag after using it
      localStorage.removeItem('showDashboard');
    }
  }, []);

  const handleViewToggle = (view) => {
    setActiveView(view);
  };

  return (
    <div className="App">
      <div className="user-header">
        <div className="user-welcome">
          Welcome back, <strong>{currentUser?.displayName || userProfile?.displayName || 'Trader'}</strong>! 🚀
        </div>
        <div className="header-actions">
          <div className="view-toggle-container">
            <div className="view-toggle">
              <button
                className={`toggle-option ${activeView === 'crypto' ? 'active' : ''}`}
                onClick={() => handleViewToggle('crypto')}
              >
                <span className="toggle-icon">📈</span>
                <span className="toggle-label">Crypto Markets</span>
              </button>
              <button
                className={`toggle-option ${activeView === 'dashboard' ? 'active' : ''}`}
                onClick={() => handleViewToggle('dashboard')}
              >
                <span className="toggle-icon">💰</span>
                <span className="toggle-label">My Dashboard</span>
              </button>
              <div className={`toggle-slider ${activeView === 'dashboard' ? 'slide-right' : 'slide-left'}`}></div>
            </div>
          </div>
          <button onClick={signOut} className="sign-out-button">
            Log Out
          </button>
        </div>
      </div>
      {activeView === 'dashboard' ? <UserDashboard /> : <Home />}
    </div>
  );
};

const AppContent = () => {
  const { isAuthenticated, loading, currentUser, userProfile } = useAuth();
  const [showHomepage, setShowHomepage] = useState(true);

  const handleAuthenticated = () => {
    setShowHomepage(false);
  };

  // Force re-render when user profile changes (especially after onboarding completion)
  useEffect(() => {
    if (isAuthenticated && currentUser && userProfile?.onboardingCompleted) {
      setShowHomepage(false);
    }
  }, [isAuthenticated, currentUser, userProfile]);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Check if user needs to complete onboarding
  if (isAuthenticated && currentUser && !userProfile?.onboardingCompleted) {
    return <Onboarding />;
  }

  // If user is authenticated and has completed onboarding, show the dashboard
  if (isAuthenticated && currentUser && userProfile?.onboardingCompleted) {
    return <ProtectedDashboard />;
  }

  // Show homepage for non-authenticated users or when showHomepage is true
  if (!isAuthenticated || showHomepage) {
    return <Homepage onAuthenticated={handleAuthenticated} />;
  }

  return <ProtectedDashboard />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;